/*
 CONTRATO DE DIRECCIÓN — Soham Home, «La pared de material»

 THESIS: el catálogo ES la interfaz. Se toca material, no se lee una lista. Rechaza
   de plano el renglón-con-miniatura —nombre a la izquierda, importe a la derecha,
   hilo gris debajo— que convertía la propuesta en estado de cuenta, y rechaza el
   lienzo blanco vacío: la calma de la referencia venía de la fotografía a sangre,
   no del blanco, y sin imagen el blanco es una hoja en blanco.
 OWN-WORLD: mosaicos a sangre sobre una familia de tinta verde-negra (#141E1A), cada
   uno un recorte del material a escala —celosía de nogal, cobre de la tierra física,
   ámbar de la lámpara, galvanizado del tablero—; página en hueso; verde sabio
   #059669 RESERVADO a la acción, al precio y a la selección, una sola vez por
   pantalla; DM Sans, servida desde el repo porque el trabajo sin conexión es
   restricción dura. Sin bordes de tarjeta, sin versales espaciadas, sin sombras.
 STORY: Juan sostiene la tableta con una mano en casa ajena; el cliente de 55 a 75
   años alcanza a ver material reconocible, no software; el fabricante reconoce el
   corte de su propia CNC en el mosaico.
 FIRST VIEWPORT: la aplicación abre preguntando QUÉ SE VA A HACER —cotización nueva,
   con el material encima, o abrir una existente—, no con el archivo de clientes. Ese
   registro sigue estando, un toque más adentro. Dentro de una cotización manda la
   pared de mosaicos, dos columnas en teléfono y hasta seis en tableta, con el nombre
   encima del material; la barra de trabajo fija abajo con la cota y el importe.
 FORM: dirección PINADA por Juan el 2026-08-10 —«que se vea como la página de Soham,
   para táctil, súper gráfico, que ocupe toda la pantalla»— y corregida por él el
   2026-08-13 al verla en monitor: **el contenido vive en una columna centrada de
   1180 px, la misma del sitio**, y solo la pared de mosaicos sangra, y solo cuando la
   pantalla es más angosta que esa columna. «A sangre» aplicado a toda la interfaz
   dejaba el texto flotando de borde a borde en un monitor ancho.
   Sin tirada de concurso: el brief fija el mundo. Reemplaza a «Warm Minimalist
   Luxury», cuya ejecución entregó la versión más blanda del mundo Sonos: lienzo
   blanco, tipografía de formulario y mosaicos de icono en gris.
 FINISH: unreviewed and undocumented is unfinished; this build ends with the finish
   review, the verdict, and DESIGN.md
*/

import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import * as store from './datos/store.js'
import { usuario, claimsActuales } from './firebase.js'
import { CATEGORIAS, DISPOSITIVOS, PRODUCTOS, PAQUETES, EQUIPOS } from './motor/catalogo.js'
/* La familia técnica de insumos —cable por calibre y aislamiento, tubo, boquillas,
   bombas— y las preferencias de obra de Juan. Se siembran igual que el resto del
   catálogo: a partir de la primera vez viven en el almacén y él los edita. */
import { INSUMOS_TECNICOS, PREFERENCIAS_BASE } from './motor/catalogo-tecnico.js'
/* El último eslabón: convertir un levantamiento en un renglón cotizable. */
import { TALLER_BASE } from './motor/taller.js'
import { riegoAProducto, electricoAProducto, camarasAProducto, wifiAProducto, iluminacionAProducto, tallerAProducto, RENDIMIENTOS_BASE } from './motor/levantamiento.js'
import { PROTOCOLO_REVISION, HERRAMIENTAS_SEMILLA } from './motor/protocolos.js'
import { normalizarPlano, planoParaMotor, parcheDesdeMotor, RECINTO_DEL_MOTOR, habitacionesDesdePlano, VERSION_PLANO } from './motor/plano.js'
import VistaPlano from './ui/VistaPlano.jsx'
import PaletaElectrica, { resumenObjetos } from './ui/Paleta.jsx'
import { partidasDeRecinto, aplicarPartidas } from './motor/partidas.js'
import Cierre from './ui/Cierre.jsx'

/** Las intervenciones de UNA casa (bitácora): el cierre y la propuesta las necesitan
    para decir solo lo que es cierto — si hubo Revisión, si hay dictamen. */
function useIntervencionesDeCasa(casaId) {
  const [lista, setLista] = useState([])
  useEffect(() => {
    if (!casaId) { setLista([]); return undefined }
    return store.suscribirConsulta('intervenciones', 'casa', casaId, setLista)
  }, [casaId])
  return lista
}
import { CATEGORIAS_TALLER, PRODUCTOS_TALLER } from './motor/catalogo-taller.js'
import { piezasDeRamo, RAMOS } from './motor/encargos.js'
import { VistaEncargoTaller, SelloEstado } from './ui/Encargo.jsx'
import Bodega from './ui/Bodega.jsx'
import VistaBitacora from './ui/Bitacora.jsx'
import { BotonSalir } from './ui/Sesion.jsx'
import { DESPIECES_MUESTRA, PRODUCTOS_MUESTRA } from './motor/despieces-muestra.js'
import {
  contexto, porProducto, porCotizacion, rentabilidad, subtotal,
  itemsDeEtapa, FACTORES_BASE
} from './motor/calculo.js'
import Documentos from './ui/Documentos.jsx'
import Riego from './ui/Riego.jsx'
import Electrico from './ui/Electrico.jsx'
import Camaras from './ui/Camaras.jsx'
import Wifi from './ui/Wifi.jsx'
import Iluminacion from './ui/Iluminacion.jsx'
import Taller from './ui/Taller.jsx'
import Sello from './ui/Sello.jsx'
import Firma from './ui/Firma.jsx'
import Configuracion from './ui/Configuracion.jsx'
import { esOpciones, rotulo, rotuloCorto, abrev } from './bloques.js'
import { fotoDe, FOTO_PORTADA, FOTO_RUBRO } from './fotos.js'
import { ASPERSORES } from './motor/riego.js'
import { CAMARAS } from './motor/camaras.js'
import { PUNTOS_ACCESO } from './motor/wifi.js'
import { semillaDe } from './motor/semillas.js'
import { PATRON_CELOSIA } from './motor/graficos.js'

/** Patrón del taller para las superficies que no llevan fotografía. Se calcula una vez. */
const PATRON = PATRON_CELOSIA(72, '#8A5A34')

/** Catálogo completo: smart home y obra eléctrica, más las 95 piezas del taller. */
const TODAS_CATEGORIAS = [...CATEGORIAS, ...CATEGORIAS_TALLER]
const TODOS_PRODUCTOS  = [...PRODUCTOS, ...PRODUCTOS_TALLER, ...PRODUCTOS_MUESTRA]

/* Profundidad de cada vista en el árbol de navegación. Decide la DIRECCIÓN de la
   transición al cambiar de vista (Regla 3 de la teoría de diseño): a más hondo se
   entra desde la derecha, de regreso se entra desde la izquierda. */
const PROFUNDIDAD = {
  inicio: 0,
  cotizaciones: 1, clientes: 1, levantamientos: 1, encargos: 1, catalogo: 1, config: 1,
  nueva: 2, 'nueva-cliente': 3, 'nueva-existente': 3, 'nueva-casa': 4, quien: 2, rubros: 6, plano: 5,
  cliente: 2, casa: 3, bitacora: 4,
  cot: 5, ajustes: 6, notas: 6, firma: 6, docs: 6, cierre: 6, bodega: 6, 'encargo-taller': 6, hab: 6,
  add: 7, electrico: 7, riego: 7, camaras: 7, wifi: 7, iluminacion: 7, taller: 7
}

/**
 * Un renglón tocable es también un botón para el teclado y para el lector de
 * pantalla, no solo para el dedo: rol, foco y Enter/espacio. Se aplica a todo
 * `div` con `tap` que navegue.
 */
const tapProps = onClick => ({
  role: 'button', tabIndex: 0, onClick,
  onKeyDown: e => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() }
  }
})

/** Los seis levantamientos guiados. La ficha grande vive en el módulo del inicio;
    dentro de la habitación van como fila compacta, después del contenido propio. */
const LEVANTAMIENTOS = [
  { p: 'electrico', n: 'Revisión eléctrica' },
  { p: 'riego', n: 'Riego de jardín' },
  { p: 'camaras', n: 'Videovigilancia' },
  { p: 'wifi', n: 'Wi-Fi' },
  { p: 'iluminacion', n: 'Iluminación' },
  { p: 'taller', n: 'Fabricación en madera' }
]

/**
 * LA PARED DE RUBROS — la puerta de la visita (Teoría §1 v2 y §7, aprobada por Juan
 * el 2026-08-20). El vendedor llega por un motivo concreto y toca por lo que lo
 * llamaron. Cada rubro sabe a qué motor va y cómo se llama la habitación que
 * recibe su partida; Smart home no tiene motor: abre el catálogo en su categoría.
 */
const RUBROS = [
  /* Eléctrico ENTRA AL PLANO (E1): se recorre la casa recinto por recinto; la
     Revisión con instrumentos —que la hace el ingeniero— se abre desde ahí. */
  { id: 'electrico', p: 'plano', nombre: 'Eléctrico', desc: 'Recorre la casa y dibuja el plano',
    hab: 'Instalación eléctrica', img: CATEGORIAS.find(c => c.id === 'tab')?.img },
  { id: 'smart', p: 'add', cat: 'aut', nombre: 'Smart home', desc: 'Automatización por habitación',
    hab: 'Smart home', img: fotoDe('aut') },
  { id: 'taller', p: 'taller', nombre: 'Madera y acero', desc: 'Fabricación a la medida',
    hab: 'Fabricación', img: fotoDe('arq') },
  { id: 'riego', p: 'riego', nombre: 'Riego', desc: 'El jardín, zona por zona',
    hab: 'Jardín', img: FOTO_RUBRO.riego },
  { id: 'camaras', p: 'camaras', nombre: 'Videovigilancia', desc: 'El predio y sus cámaras',
    hab: 'Exterior', img: FOTO_RUBRO.camaras },
  { id: 'wifi', p: 'wifi', nombre: 'Wi-Fi', desc: 'Cobertura en toda la casa',
    hab: 'Toda la casa', img: FOTO_RUBRO.wifi },
  { id: 'iluminacion', p: 'iluminacion', nombre: 'Iluminación', desc: 'Luz y control por espacio',
    hab: 'Iluminación', img: fotoDe('ilu') }
]

/**
 * LA NAVEGACIÓN PERSISTENTE (Juan, 2026-08-22): «es más conveniente tener un
 * sidebar». En escritorio es una barra lateral; en teléfono, pestañas abajo. Solo
 * en las vistas de primer nivel: dentro de una tarea (cotización, plano, motor)
 * manda la barra de trabajo y se vuelve con «Regresar». Es el esqueleto de
 * SkyPel con el contenido de la teoría: las secciones siempre a un toque.
 */
/* Iconos de la navegación: trazo delgado, 1.5 px, como el resto (manual de marca §5). */
const ICONOS_NAV = {
  inicio: <><path d="M3 11l9-8 9 8" /><path d="M5 10v10h14V10" /></>,
  cotizaciones: <><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></>,
  clientes: <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-5 6-5s6 1.7 6 5" /><circle cx="17" cy="9" r="2.5" /><path d="M16 15c3 0 5 2 5 5" /></>,
  encargos: <><path d="M3 8l9-5 9 5v8l-9 5-9-5z" /><path d="M3 8l9 5 9-5M12 13v8" /></>,
  levantamientos: <><path d="M4 20L20 4" /><path d="M7 17l2 2M10 14l2 2M13 11l2 2M16 8l2 2" /></>,
  catalogo: <><rect x="3" y="3" width="8" height="8" rx="1.5" /><rect x="13" y="3" width="8" height="8" rx="1.5" /><rect x="3" y="13" width="8" height="8" rx="1.5" /><rect x="13" y="13" width="8" height="8" rx="1.5" /></>,
  config: <><path d="M4 7h10M18 7h2M4 12h2M10 12h10M4 17h8M16 17h4" /><circle cx="16" cy="7" r="2" /><circle cx="8" cy="12" r="2" /><circle cx="14" cy="17" r="2" /></>
}
const NAV = [
  { id: 'inicio', nombre: 'Inicio', roles: ['admin', 'gerente', 'vendedor'] },
  { id: 'cotizaciones', nombre: 'Cotizaciones', roles: ['admin', 'gerente', 'vendedor'] },
  { id: 'clientes', nombre: 'Clientes', roles: ['admin', 'gerente', 'vendedor'] },
  { id: 'encargos', nombre: 'Encargos', roles: ['admin', 'gerente', 'vendedor'] },
  { id: 'levantamientos', nombre: 'Practicar', roles: ['admin', 'gerente', 'vendedor'], soloEscritorio: true },
  { id: 'catalogo', nombre: 'Catálogo', roles: ['admin'] },
  { id: 'config', nombre: 'Ajustes', roles: ['admin'] }
]
function Nav({ rol, activa, onIr }) {
  const items = NAV.filter(i => i.roles.includes(rol))
  /* Mientras la barra está montada, el cuerpo lo sabe: en escritorio el contenido y
     la barra de trabajo se corren a la derecha de la columna (CSS `body.con-nav`). */
  useEffect(() => {
    document.body.classList.add('con-nav')
    return () => document.body.classList.remove('con-nav')
  }, [])
  return (
    <nav className="nav" aria-label="Secciones">
      <div className="nav-marca">Soham Central</div>
      {items.map(i => (
        <button type="button" key={i.id} className={`${activa === i.id ? 'on' : ''}${i.soloEscritorio ? ' solo-escritorio' : ''}`}
          aria-current={activa === i.id ? 'page' : undefined} aria-label={i.nombre} onClick={() => onIr(i.id)}>
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICONOS_NAV[i.id]}</svg>
          <span>{i.nombre}</span>
        </button>
      ))}
    </nav>
  )
}

/**
 * EL RESUMEN DEL INICIO (Juan, 2026-08-22): «cuántas cotizaciones están abiertas,
 * cuántos proyectos pendientes, qué necesito hacer». Tres cifras y la lista de lo
 * que toca, cada renglón tocable. Los encargos se suscriben aquí (solo vendedor,
 * gerente y admin pueden leer la colección completa).
 */
function Resumen({ cotizaciones, recorridos, onIr, onRetomar }) {
  const [encargos, setEncargos] = useState([])
  useEffect(() => store.suscribir('encargos', setEncargos), [])
  const abiertas = cotizaciones.filter(c => !c.estado || c.estado === 'borrador')
  const porPresentar = abiertas.filter(c => !c.aprobacion && !c.firmaCliente && (c.habitaciones || []).some(h => (h.items || []).length))
  const obras = cotizaciones.filter(c => c.estado === 'aceptada' || c.estado === 'en_obra')
  const propuestasTaller = encargos.filter(e => e.estado === 'propuesta')
  const enTaller = encargos.filter(e => e.estado === 'levantado' || e.estado === 'desarrollo')
  const pendientes = recorridos.filter(r => cotizaciones.some(c => c.id === r.cot)).slice(0, 2)
  const n = (k, s, p) => `${k} ${k === 1 ? s : p}`
  const toca = [
    ...pendientes.map(r => ({ k: `r-${r.id}`, t: `Retomar: ${r.nombreCasa || 'la visita'}${r.nombreRecinto ? ` — ${r.nombreRecinto}` : ''}`, s: 'Te quedaste a medias. Toca para seguir donde ibas.', go: () => onRetomar(r) })),
    propuestasTaller.length ? { k: 'prop', t: n(propuestasTaller.length, 'propuesta del taller por revisar', 'propuestas del taller por revisar'), s: propuestasTaller.map(e => e.nombreProyecto).slice(0, 3).join(' · '), go: () => onIr('encargos') } : null,
    porPresentar.length ? { k: 'pres', t: n(porPresentar.length, 'cotización por presentar', 'cotizaciones por presentar'), s: porPresentar.map(c => c.nombre).slice(0, 3).join(' · '), go: () => onIr('cotizaciones') } : null,
    enTaller.length ? { k: 'tall', t: n(enTaller.length, 'encargo en el taller', 'encargos en el taller'), s: enTaller.map(e => e.nombreProyecto).slice(0, 3).join(' · '), go: () => onIr('encargos') } : null
  ].filter(Boolean)
  return (
    <>
      <div className="cifras">
        <button type="button" className="cifra" onClick={() => onIr('cotizaciones')}>
          <b>{abiertas.length}</b><span>{abiertas.length === 1 ? 'cotización abierta' : 'cotizaciones abiertas'}</span>
        </button>
        <button type="button" className="cifra" onClick={() => onIr('cotizaciones')}>
          <b>{obras.length}</b><span>{obras.length === 1 ? 'obra en curso' : 'obras en curso'}</span>
        </button>
        <button type="button" className="cifra" onClick={() => onIr('encargos')}>
          <b>{encargos.filter(e => e.estado !== 'cotizado').length}</b><span>encargos vivos</span>
        </button>
      </div>
      <div className="sec">Qué toca ahora</div>
      {toca.length ? toca.map(x => (
        <div className="card tap" key={x.k} {...tapProps(x.go)}>
          <div className="grow"><div className="t">{x.t}</div><div className="s">{x.s}</div></div>
          <div className="chev"><IconoAvanza /></div>
        </div>
      )) : (
        <div className="empty" style={{ maxWidth: 'none' }}><b>Nada pendiente.</b><p>Sin recorridos a medias, sin propuestas del taller por revisar, sin cotizaciones sin presentar.</p></div>
      )}
    </>
  )
}

/**
 * PROSPECTOS DEL SITIO (2026-08-23): lo que llega del formulario de sohamiot.com
 * (colección `prospectos`, escrita sin sesión por REST; reglas en firestore.rules).
 * Aquí se atienden: WhatsApp o correo a un toque y «Atendido» para sacarlo de la lista.
 * Solo vendedor/gerente/admin los leen; si las reglas rechazan, llega vacío y no se pinta.
 */
function Prospectos() {
  const [lista, setLista] = useState([])
  useEffect(() => store.suscribir('prospectos', setLista), [])
  const vivos = lista.filter(p => p.estado !== 'atendido' && p.nombre).sort((a, b) => (b.creadoEn || '').localeCompare(a.creadoEn || ''))
  if (!vivos.length) return null
  const fecha = iso => { try { return new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) } catch { return '' } }
  const tel = p => (p.telefono || '').replace(/\D/g, '')
  const atender = p => store.guardarParcial('prospectos', p.id, { estado: 'atendido', atendidoPor: usuario()?.uid || 'local', atendidoEn: new Date().toISOString() })
  return (
    <>
      <div className="sec">Prospectos del sitio · {vivos.length}</div>
      {vivos.slice(0, 10).map(p => (
        <div className="card" key={p.id}>
          <div className="grow">
            <div className="t">{p.nombre}{p.empresa ? ` · ${p.empresa}` : ''}</div>
            <div className="s">{[p.origen, fecha(p.creadoEn), p.telefono, p.correo].filter(Boolean).join(' · ')}</div>
            {p.mensaje && <div className="s" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{p.mensaje.slice(0, 400)}</div>}
            {p.detalle && <div className="s" style={{ marginTop: 6, opacity: .75 }}>{Object.entries(p.detalle).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ').slice(0, 300)}</div>}
          </div>
          <div className="fila-pills" style={{ marginTop: 10 }}>
            {tel(p) && <a className="ib sm pri" href={`https://wa.me/${tel(p).length === 10 ? '52' + tel(p) : tel(p)}?text=${encodeURIComponent(`Hola ${p.nombre}, soy Juan de Soham. Recibí tu mensaje en sohamiot.com.`)}`} target="_blank" rel="noreferrer">WhatsApp</a>}
            {p.correo && <a className="ib sm" href={`mailto:${p.correo}?subject=${encodeURIComponent('Soham — tu mensaje en sohamiot.com')}`}>Correo</a>}
            <button type="button" className="ib sm" onClick={() => atender(p)}>Atendido</button>
          </div>
        </div>
      ))}
    </>
  )
}

function ParedDeRubros({ onElegir }) {
  return (
    <div className="grid sangre">
      {RUBROS.map(r => (
        <button type="button" className="gc" key={r.id} onClick={() => onElegir(r)}
          aria-label={`${r.nombre} — ${r.desc}`}>
          <img className="ph" alt="" src={r.img} loading="lazy" />
          <div className="b"><div className="n">{r.nombre}</div><div className="s">{r.desc}</div></div>
        </button>
      ))}
    </div>
  )
}

/** Nombre por defecto de la casa y de la cotización: «Casa <apellido>». Se puede cambiar. */
const nombrePropuesto = nombreCliente => 'Casa ' + (nombreCliente || '').trim().split(' ').pop()

/**
 * «¿PARA QUIÉN?» — el alta en UNA tarjeta (Teoría §1 v2, punto 2). El nombre busca
 * clientes existentes mientras se teclea; si hay coincidencia se toca y aparecen sus
 * casas; si no, con el nombre y la casa pre-llenada basta. Un solo «Empezar».
 * Evidencia: GOV.UK «one thing per page» y NN/g (carga cognitiva) — para un usuario
 * de baja confianza digital, una tarjeta vence a una cadena de tres pasos.
 */
function Quien({ rubro, clientes, casas, onEmpezar, atras }) {
  const [nombre, setNombre] = useState('')
  const [cli, setCli] = useState(null)
  const [casaSel, setCasaSel] = useState(null)
  const [alias, setAlias] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const t = nombre.trim().toLowerCase()
  const coincidencias = !cli && t.length >= 2
    ? clientes.filter(c => (c.nombre || '').toLowerCase().includes(t)).slice(0, 5) : []
  const susCasas = cli ? casas.filter(x => x.cliente === cli.id) : []
  /* Si el cliente tiene UNA sola casa, es esa: no se pregunta (Juan, 2026-08-22). */
  const elegirCliente = c => {
    setCli(c); setNombre(c.nombre)
    const suyas = casas.filter(x => x.cliente === c.id)
    setCasaSel(suyas.length === 1 ? suyas[0] : null)
  }
  const aliasFinal = alias.trim() || nombrePropuesto(nombre)
  const listo = nombre.trim().length > 0 && (casaSel || aliasFinal.length > 5)
  return (
    <div className="app">
      <Barra titulo="¿Para quién?" migaja={rubro ? rubro.nombre : 'Cotización nueva'} atras={atras} />
      <main>
        <div className="card captura">
          <label className="f">
            <span>Nombre del cliente</span>
            <input autoFocus value={nombre} placeholder="Como se presenta"
              onChange={e => { setNombre(e.target.value); setCli(null); setCasaSel(null) }} />
          </label>
          {coincidencias.map(c => (
            <div className="card tap" key={c.id} {...tapProps(() => elegirCliente(c))}>
              <div className="grow">
                <div className="t">{c.nombre}</div>
                <div className="s">{plural(casas.filter(x => x.cliente === c.id).length, 'casa registrada', 'casas registradas')}</div>
              </div>
              <div className="chev"><IconoAvanza /></div>
            </div>
          ))}
          {cli && susCasas.length > 0 && (
            <label className="f">
              <span>{susCasas.length === 1 ? 'Su casa (toca para cambiarla)' : 'Sus casas — toca una, o registra otra abajo'}</span>
              <div className="etapas" style={{ marginTop: 8 }}>
                {susCasas.map(x => (
                  <button type="button" key={x.id} className={`et ${casaSel?.id === x.id ? 'on1' : ''}`}
                    onClick={() => setCasaSel(casaSel?.id === x.id ? null : x)}>{x.alias}</button>
                ))}
              </div>
            </label>
          )}
          {!casaSel && (
            <label className="f" style={{ marginBottom: 0 }}>
              <span>{susCasas.length ? 'Otra casa' : 'La casa'}</span>
              <input value={alias} placeholder={nombre.trim() ? nombrePropuesto(nombre) : 'Casa…'}
                onChange={e => setAlias(e.target.value)} />
            </label>
          )}
          <div className="captura-acc">
            <button type="button" className="ib" onClick={atras}>Cancelar</button>
            <button type="button" className="ib pri" disabled={!listo || ocupado}
              onClick={async () => {
                setOcupado(true)
                await onEmpezar({ cli, nombre: nombre.trim(), casaSel, alias: aliasFinal })
              }}>Empezar</button>
          </div>
        </div>
        <div className="nota">
          Con el nombre y la casa basta: teléfono, correo y dirección se llenan después,
          desde la ficha del cliente. La cotización se abre con el nombre de la casa.
        </div>
      </main>
    </div>
  )
}

const mx = n => '$' + Math.round(n || 0).toLocaleString('es-MX')
const pct = n => (n || 0).toFixed(1) + '%'
const hoy = () => new Date().toISOString().slice(0, 10)

/** Plural resuelto, no «producto(s)». Es una herramienta que se le enseña a un cliente. */
const plural = (n, singular, plural_) => `${n} ${n === 1 ? singular : plural_}`

/**
 * Iconos. Trazo delgado, como pide el manual de marca (§5): «precisión micrométrica,
 * nunca grosor pesado». Van dibujados y no como carácter tipográfico porque `▦` salía
 * como cuadro de sustitución en la fuente del sistema.
 */
const Icono = ({ d, etiqueta }) => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    focusable="false" role={etiqueta ? 'img' : undefined}>{d}</svg>
)
const IconoAjustes = () => (
  <Icono d={<>
    <path d="M4 7h10M18 7h2M4 12h2M10 12h10M4 17h8M16 17h4" />
    <circle cx="16" cy="7" r="2" /><circle cx="8" cy="12" r="2" /><circle cx="14" cy="17" r="2" />
  </>} />
)
const IconoMas = () => <Icono d={<path d="M12 5v14M5 12h14" />} />
/* Los chevrones iban como caracteres «‹» y «›»: en la fuente del sistema salían
   diminutos dentro de su píldora y se leían como un icono roto. Van dibujados. */
const IconoAtras = () => <Icono d={<path d="M15 5l-7 7 7 7" />} />
const IconoAvanza = () => <Icono d={<path d="M9 5l7 7-7 7" />} />
const IconoBuscar = () => <Icono d={<><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.6-3.6" /></>} />

/** Buscador. El campo desnudo se leía como texto suelto, no como control. */
function Buscador({ valor, onCambio, etiqueta }) {
  return (
    <div className="buscador">
      <IconoBuscar />
      <input placeholder={etiqueta} aria-label={etiqueta}
        value={valor} onChange={e => onCambio(e.target.value)} />
    </div>
  )
}

/** Recuadro de imagen. Sin foto, queda la placa dibujada: nunca un icono roto. */
function Lamina({ src, clase = 'ph' }) {
  return src ? <img className={clase} alt="" src={src} /> : <div className={clase} />
}

/* ============================ Datos de la aplicación ============================ */

/** Cerrojo de módulo: la semilla corre una sola vez aunque el efecto se monte dos veces. */
let sembrando = null

function useDatos() {
  const [clientes, setClientes] = useState([])
  const [casas, setCasas] = useState([])
  const [recientes, setRecientes] = useState([])
  const [recorridos, setRecorridos] = useState([])
  const [cotizaciones, setCotizaciones] = useState([])
  /* El rol vive aquí y no en App porque DECIDE CÓMO SE SUSCRIBE: las reglas solo
     dejan a la cuadrilla leer cotizaciones de obra, y en Firestore las reglas no
     filtran — rechazan la consulta completa. Suscribir la colección entera con
     una sesión de cuadrilla rompería su arranque. */
  const [rol, setRol] = useState(null)
  const [productos, setProductos] = useState([])
  const [dispositivos, setDispositivos] = useState([])
  const [herramientas, setHerramientas] = useState([])
  const [protocolos, setProtocolos] = useState([])
  const [config, setConfig] = useState([])
  const [despieces, setDespieces] = useState([])
  const [listo, setListo] = useState(false)

  useEffect(() => {
    // Semilla la primera vez. A partir de ahí el catálogo y los factores viven en
    // los datos y se editan desde la aplicación: NO son constantes del código.
    //
    // La semilla es IDEMPOTENTE: cada producto e insumo se guarda con su sku como
    // identificador, así que correrla dos veces sobrescribe en vez de duplicar. Hacía
    // falta porque React ejecuta este efecto dos veces en desarrollo y la primera
    // versión, con identificadores aleatorios, sembró el catálogo por duplicado.
    ;(async () => {
      if (!sembrando) {
        sembrando = (async () => {
          /* La comprobación va por LISTAS, no documento por documento: la versión
             anterior hacía ~200 `obtener` secuenciales en cada dispositivo nuevo
             — en un teléfono eran más de un minuto de «Cargando…», y iOS suspende
             el PWA antes de terminar (caso real: el primer arranque de producción,
             2026-08-19, se quedó atorado en el producto 16). Siete consultas y
             el resto se decide en memoria. */
          const [cfgs, prods, disps, desps, prots, herrs] = await Promise.all([
            store.listar('config'),
            store.listar('catalogoProductos'),
            store.listar('catalogoDispositivos'),
            store.listar('despieces'),
            store.listar('catalogoProtocolos'),
            store.listar('catalogoHerramientas')
          ])
          const cfg = new Set(cfgs.map(x => x.id))
          if (!cfg.has('factores')) {
            await store.guardar('config', { id: 'factores', ...FACTORES_BASE })
            await store.guardar('config', { id: 'equipos', lista: EQUIPOS })
          }
          if (!cfg.has('preferencias')) await store.guardar('config', { id: 'preferencias', ...PREFERENCIAS_BASE })
          if (!cfg.has('rendimientos')) await store.guardar('config', { id: 'rendimientos', ...RENDIMIENTOS_BASE })
          if (!cfg.has('taller')) await store.guardar('config', { id: 'taller', ...TALLER_BASE })

          const prodPorSku = new Map(prods.map(x => [x.sku || x.id, x]))
          for (const p of TODOS_PRODUCTOS) {
            const previo = prodPorSku.get(p.sku)
            if (!previo) {
              await store.guardar('catalogoProductos', { ...p, id: p.sku })
            } else if (p.explica && !previo.explica) {
              /* MIGRACIÓN (2026-08-18, mismo patrón que `tec`): se copia SOLO
                 `explica` y solo si está vacío — lo que Juan escribió, manda. */
              await store.guardar('catalogoProductos', { ...previo, id: p.sku, explica: p.explica })
            }
          }

          const despSet = new Set(desps.map(x => x.id))
          for (const [sku, d] of Object.entries(DESPIECES_MUESTRA)) {
            if (!despSet.has(sku)) await store.guardar('despieces', { ...d, id: sku })
          }

          const dispPorSku = new Map(disps.map(x => [x.sku || x.id, x]))
          for (const d of [...DISPOSITIVOS, ...INSUMOS_TECNICOS]) {
            const previo = dispPorSku.get(d.sku)
            if (!previo) {
              await store.guardar('catalogoDispositivos', { ...d, id: d.sku })
            } else if (d.tec && !previo.tec) {
              /* MIGRACIÓN del enganche técnico (`CBL-12`/`CBL-10`): se copia
                 ÚNICAMENTE `tec` — el costo, el tipo y el IVA son de Juan. */
              await store.guardar('catalogoDispositivos', { ...previo, id: d.sku, tec: d.tec })
            }
          }

          // Limpieza de los duplicados con identificador aleatorio de versiones previas
          for (const x of prods) {
            if (x.sku && x.id !== x.sku) await store.borrar('catalogoProductos', x.id)
          }
          for (const x of disps) {
            if (x.sku && x.id !== x.sku) await store.borrar('catalogoDispositivos', x.id)
          }

          /* Semilla del primer protocolo guiado: la Revisión, con sus tres
             herramientas. Si Juan lo borra o lo cambia, la semilla NO lo repone. */
          if (!prots.some(p => p.id === PROTOCOLO_REVISION.id)) {
            const herrSet = new Set(herrs.map(h => h.id))
            for (const h of HERRAMIENTAS_SEMILLA) {
              if (!herrSet.has(h.id)) await store.guardar('catalogoHerramientas', h)
            }
            await store.guardar('catalogoProtocolos', PROTOCOLO_REVISION)
          }
          /* MIGRACIÓN Cliente→Casa (2026-08-18): la cotización pasa a colgar de una
             casa, porque un cliente tendrá varias. Toda cotización sin `casa` recibe
             la «Casa principal» de su cliente, creada al vuelo con la dirección que
             el cliente tuviera. Se hizo ANTES de que existiera operación real a
             propósito: con la nube vacía esto es un toque; con clientes reales
             dentro habría sido cirugía. El campo `cliente` de la cotización se
             conserva por compatibilidad. */
          {
            const cots = await store.listar('cotizaciones')
            const sinCasa = cots.filter(c => c.cliente && !c.casa)
            if (sinCasa.length) {
              const porCliente = new Map()
              for (const casa of await store.listar('casas'))
                if (!porCliente.has(casa.cliente)) porCliente.set(casa.cliente, casa.id)
              for (const cot of sinCasa) {
                let casaId = porCliente.get(cot.cliente)
                if (!casaId) {
                  const cli = await store.obtener('clientes', cot.cliente)
                  casaId = await store.guardar('casas', {
                    cliente: cot.cliente, alias: 'Casa principal',
                    direccion: cli?.direccion || '', ubicacion: '', notasAcceso: ''
                  })
                  porCliente.set(cot.cliente, casaId)
                }
                await store.guardar('cotizaciones', { ...cot, casa: casaId })
              }
            }
          }
        })().catch(e => console.error('La semilla no terminó:', e))
      }
      /* ARRANQUE OPTIMISTA (2026-08-19): la app abre aunque la semilla no haya
         terminado — hay catálogo de respaldo en memoria (`TODOS_PRODUCTOS`) y la
         semilla sigue corriendo en el fondo. Sin este tope, cualquier lectura
         colgada (señal mala, caché de Firestore dañada por una suspensión de
         iOS) dejaba la pantalla en «Cargando…» para siempre — caso real del
         primer arranque en el teléfono de Juan. */
      await Promise.race([sembrando, new Promise(r => setTimeout(r, 8000))])
      setListo(true)
    })()
    const subs = [
      store.suscribir('clientes', setClientes),
      store.suscribir('casas', setCasas),
      store.suscribir('catalogoProductos', setProductos),
      store.suscribir('catalogoDispositivos', setDispositivos),
      store.suscribir('catalogoHerramientas', setHerramientas),
      store.suscribir('catalogoProtocolos', setProtocolos),
      store.suscribir('config', setConfig),
      store.suscribir('despieces', setDespieces),
      /* «Recientes» es PERSONAL, no global: cada quien ve las cotizaciones que ÉL
         abrió. Vive en el estado del usuario (usuarios/{uid}/estado); en modo local,
         bajo la llave 'local' del navegador. */
      store.suscribir(`usuarios/${usuario()?.uid || 'local'}/estado`, docs => {
        setRecientes(docs.find(d => d.id === 'recientes')?.lista || [])
        /* E3: los cursores de recorrido interrumpido («Retomar») viven en el mismo
           estado personal, uno por cotización: `recorrido-{cotId}`. */
        setRecorridos(docs.filter(d => d.id.startsWith('recorrido-'))
          .sort((a, b) => (b.actualizadoEn || '').localeCompare(a.actualizadoEn || '')))
      })
    ]
    /* Cotizaciones: la suscripción espera al rol. La cuadrilla solo puede leer
       obra (aceptada / en obra / terminada) — se le suscribe UNA consulta por
       estado permitido y se funden; a todos los demás, la colección completa. */
    let vivo = true
    claimsActuales().catch(() => ({})).then(c => {
      if (!vivo) return
      const r = c.rol || 'admin'
      setRol(r)
      if (r === 'cuadrilla') {
        const porEstado = new Map()
        for (const estado of ['aceptada', 'en_obra', 'terminada']) {
          subs.push(store.suscribirConsulta('cotizaciones', 'estado', estado, lista => {
            porEstado.set(estado, lista)
            setCotizaciones([...porEstado.values()].flat())
          }))
        }
      } else {
        subs.push(store.suscribir('cotizaciones', setCotizaciones))
      }
    })
    return () => { vivo = false; subs.forEach(u => u()) }
  }, [])

  const factores = { ...FACTORES_BASE, ...(config.find(c => c.id === 'factores') || {}) }
  const equipos = config.find(c => c.id === 'equipos')?.lista || EQUIPOS
  const preferencias = { ...PREFERENCIAS_BASE, ...(config.find(c => c.id === 'preferencias') || {}) }
  const rendimientos = { ...RENDIMIENTOS_BASE, ...(config.find(c => c.id === 'rendimientos') || {}) }
  const taller = { ...TALLER_BASE, ...(config.find(c => c.id === 'taller') || {}) }

  const mapaDespieces = Object.fromEntries(despieces.map(d => [d.id, d]))
  return { rol, clientes, casas, recientes, recorridos, cotizaciones, productos, dispositivos, herramientas, protocolos, factores, equipos, preferencias, rendimientos, taller, despieces: mapaDespieces, listo }
}

function useSync() {
  const [s, setS] = useState({ estado: 'local', pendientes: 0 })
  useEffect(() => store.estadoSync(setS), [])
  return s
}

/* ============================ Piezas ============================ */

function Sync({ s }) {
  const mapa = {
    local:        ['local', 'Solo en este equipo'],
    sincronizado: ['ok', 'Al día'],
    pendiente:    ['pend', 'Capturas sin subir'],
    'sin-conexion': ['off', 'Sin conexión']
  }
  /* CALLA CUANDO TODO VA BIEN (Juan, 2026-08-20: «hay un punto verde que no sé
     para qué es»). En teléfono el indicador colapsaba a un punto sin texto, y
     «al día» no es información que haya que mirar. Solo habla cuando hay algo
     que hacer o saber: capturas sin subir, sin conexión, o modo local. */
  if (s.estado === 'sincronizado') return null
  const [clase, texto] = mapa[s.estado] || mapa.local
  return <span className={`sync ${clase}`}><i />{texto}</span>
}

/**
 * Precio. Tratamiento tipográfico tomado literal del sitio de Soham Home: cifra grande
 * en Space Grotesk verde con la moneda en gris pequeño al lado.
 *
 * La moneda va SIEMPRE explícita. No es decoración: Juan presupuesta en pesos y sus
 * proveedores facturan en dólares, y un «$» solo es ambiguo justo donde más caro sale.
 */
function Precio({ valor, chico, neutro }) {
  return (
    <span className={`precio${chico ? ' chico' : ''}${neutro ? ' neutro' : ''}`}>
      <b>{mx(valor)}</b>
      <i>MXN</i>
    </span>
  )
}

/**
 * Renglón de captura.
 *
 * Sustituye a `prompt()`. La razón no es estética: el diálogo del sistema operativo
 * aparece con su propia tipografía y su propio chrome encima de la lámina, y es
 * justamente la primera acción que Juan hace de pie en casa del cliente y lo primero
 * que ve un socio al que se le enseña la herramienta. Tampoco es un modal —no hay nada
 * que proteger aquí—: es un renglón que se abre en la propia lista.
 */
function Captura({ etiqueta, inicial = '', sugerencia, accion = 'Agregar', onConfirmar, onCancelar }) {
  const [valor, setValor] = useState(inicial)
  const listo = valor.trim().length > 0
  return (
    <div className="card captura">
      <label className="f" style={{ margin: 0 }}>
        <span>{etiqueta}</span>
        <input autoFocus value={valor} placeholder={sugerencia}
          onChange={e => setValor(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && listo) onConfirmar(valor.trim())
            if (e.key === 'Escape') onCancelar()
          }} />
      </label>
      <div className="captura-acc">
        <button className="ib" onClick={onCancelar}>Cancelar</button>
        <button className="ib pri" disabled={!listo}
          onClick={() => onConfirmar(valor.trim())}>{accion}</button>
      </div>
    </div>
  )
}

/**
 * Campo de ficha que guarda al salir. Para los datos del cliente y de la casa:
 * se edita en el propio renglón, sin pantalla aparte ni botón de guardar que se
 * pueda olvidar. Solo escribe si el valor de verdad cambió.
 */
function CampoGuardado({ etiqueta, valor, onGuardar, tipo = 'text', multilinea }) {
  const [v, setV] = useState(valor ?? '')
  useEffect(() => { setV(valor ?? '') }, [valor])
  const confirmar = () => { if (v !== (valor ?? '')) onGuardar(v.trim()) }
  return (
    <label className="f">
      <span>{etiqueta}</span>
      {multilinea
        ? <textarea value={v} style={{ minHeight: 70 }}
            onChange={e => setV(e.target.value)} onBlur={confirmar} />
        : <input type={tipo} value={v}
            onChange={e => setV(e.target.value)} onBlur={confirmar} />}
    </label>
  )
}

/** Cuadro de datos de la lámina: donde un plano pone sus números. */
function Cuadro({ celdas, importe, rotuloImporte = 'Importe' }) {
  return (
    <div className="fab"><div className="in">
      <div className="cuadro">
        {celdas.map((c, i) => (
          <div className={`celda${c.chica === false ? ' oculta-chica' : ''}`} key={i}>
            <span className="k">{c.k}</span>
            <span className="v">{c.v}</span>
          </div>
        ))}
        <div className="celda importe">
          <span className="k">{rotuloImporte}</span>
          <Precio valor={importe} chico />
        </div>
      </div>
    </div></div>
  )
}

function Barra({ titulo, migaja, atras, acciones }) {
  return (
    <div className="bar">
      {atras && <button className="ib ico" onClick={atras} aria-label="Regresar"><IconoAtras /></button>}
      <h1>{titulo}{migaja && <span className="crumb">{migaja}</span>}</h1>
      {acciones}
    </div>
  )
}

function Comparativo({ cot, val, ctx }) {
  const max = Math.max(val[1].total, val[2].total, val[3].total, 1)
  const etapas = [1, 2, 3].filter(e => val[e].total > 0)
  if (!etapas.length) return <div className="empty"><b>Todavía no hay nada que comparar.</b><p>Las etapas aparecen aquí en cuanto la cotización tenga al menos una habitación con productos dentro.</p></div>
  return (
    <>
      {etapas.map(e => (
        <div className={`plan p${e}`} key={e}>
          <div className="hd">
            <div className="k">{rotulo(cot, e)}</div>
            {/* El color de la cifra lo pone la clase del bloque, en la hoja de estilos */}
            <Precio valor={val[e].total} chico />
            <div className="d">{plural(val[e].dias, 'día de obra', 'días de obra')}</div>
          </div>
          <div className="bd">
            <div className="track">
              <i style={{ width: `${Math.round((val[e].total / max) * 100)}%`, background: `var(--b${e})` }} />
            </div>
            <div className="thumbs">
              {itemsDeEtapa(cot, e).map(it => (
                <span className="thumb" key={it.sku}>
                  <Lamina src={ctx.P.get(it.sku)?.img} clase="ph xs" />
                  <b>{it.q}</b>
                </span>
              ))}
            </div>
            {val[e].complementos > 0 && <div className="ln"><span>Complementos</span><b>{mx(val[e].complementos)}</b></div>}
            {val[e].consumibles + val[e].manoObra > 0 &&
              <div className="ln"><span>Instalación</span><b>{mx(val[e].consumibles + val[e].manoObra)}</b></div>}
            {val[e].traslado + val[e].peaje > 0 &&
              <div className="ln"><span>Transporte</span><b>{mx(val[e].traslado + val[e].peaje)}</b></div>}
          </div>
        </div>
      ))}
      {/* En modo opciones el total NO se suma: el cliente elige una sola */}
      {esOpciones(cot) ? (
        <div className="card">
          <div className="s" style={{ lineHeight: 1.5 }}>
            Son <b>alternativas</b>: se contrata una de las {etapas.length}. Los importes no se
            suman entre sí.
          </div>
        </div>
      ) : etapas.length > 1 && (
        /* Con una sola etapa activa, el total del proyecto ES el de la etapa: repetir
           la misma cifra dos veces no informa, solo hace dudar de cuál es la buena. */
        <div className="plan total">
          <div className="hd">
            <div className="k">Proyecto completo</div>
            <Precio valor={val.total.total} />
            <div className="d">
              {plural(val.total.dias, 'día de obra', 'días de obra')} · {plural(etapas.length, 'etapa', 'etapas')}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/**
 * Los encargos vivos, del lado de Soham. La suscripción vive AQUÍ y no en
 * useDatos a propósito: solo vendedor, gerente y admin pueden leer la colección
 * completa — suscribirla globalmente le rompería la sesión a una cuadrilla.
 */
function ListaEncargos({ cotizaciones, onAbrir }) {
  const [encargos, setEncargos] = useState(null)
  useEffect(() => store.suscribir('encargos', lista =>
    setEncargos([...lista].sort((a, b) => (b.actualizadoEn || '').localeCompare(a.actualizadoEn || '')))), [])
  if (encargos === null) return <div className="empty">Cargando encargos…</div>
  if (!encargos.length) return (
    <div className="empty">
      <b>Todavía no hay encargos.</b>
      <p>Se crean desde una cotización con piezas del taller, con el botón
         «Encargo al taller».</p>
    </div>
  )
  return encargos.map(e => {
    const cot = cotizaciones.find(c => c.id === e.cotizacionId)
    return (
      <div className={`card${cot ? ' tap' : ''}`} key={e.id}
        {...(cot ? tapProps(() => onAbrir(e, cot)) : {})}>
        <div className="grow">
          <div className="t">{e.nombreProyecto}</div>
          <div className="s">
            {RAMOS[e.ramo]?.nombre || e.ramo} · {plural(e.piezas?.length || 0, 'pieza', 'piezas')}
            {' · '}{(e.actualizadoEn || '').slice(0, 10)}
            {!cot && ' · cotización eliminada'}
          </div>
        </div>
        <SelloEstado estado={e.estado} />
      </div>
    )
  })
}

/**
 * El inicio de la CUADRILLA: sus obras de hoy, cada una con su acción pendiente.
 * (Teoría de diseño, Regla 1 — aprobada 2026-08-20.) La cuadrilla no ve módulos ni
 * precios: llega, la app le dice qué le toca en cada obra, y un toque la lleva ahí.
 * La suscripción a bodega vive aquí y no en useDatos: solo esta pantalla la ocupa
 * completa, y las reglas dejan leerla a la sesión de obra.
 */
function InicioCuadrilla({ cotizaciones, casas, onBodega, onBitacora }) {
  const [registros, setRegistros] = useState([])
  useEffect(() => store.suscribir('bodega', setRegistros), [])
  const obras = cotizaciones.filter(c => c.estado === 'aceptada' || c.estado === 'en_obra')
  const accionDe = cot => {
    const r = registros.find(x => x.id === `bodega-${cot.id}`)
    if (!r) return 'Te toca: sacar el material de bodega'
    if (!r.fechaRegreso) return 'En obra — al terminar, registra el regreso de la herramienta'
    return 'Ciclo de bodega cerrado'
  }
  if (!obras.length) return (
    <div className="empty">
      <b>Hoy no tienes obras asignadas.</b>
      <p>Cuando una cotización se acepte y te toque, aparece aquí con lo que hay
         que hacer: sacar material, trabajar la obra y regresar la herramienta.</p>
    </div>
  )
  return (
    <>
      <div className="sec">Tus obras</div>
      {obras.map(cot => {
        const casa = casas.find(x => x.id === cot.casa)
        return (
          <div className="card" key={cot.id}>
            <div className="grow tap" {...tapProps(() => onBodega(cot))}>
              <div className="t">{casa?.alias || cot.nombre}</div>
              <div className="s">{casa?.direccion || 'Sin dirección registrada'}</div>
              <div className="s" style={{ marginTop: 6, color: 'var(--negro)' }}>
                {accionDe(cot)}
              </div>
            </div>
            <button type="button" className="discreto" onClick={() => onBitacora(cot)}>
              Bitácora y revisiones de la casa
            </button>
          </div>
        )
      })}
    </>
  )
}

/* ============================ Aplicación ============================ */

export default function App() {
  const { rol, clientes, casas, recientes, recorridos, cotizaciones, productos, dispositivos, herramientas, protocolos, factores, equipos, preferencias, rendimientos, taller, despieces, listo } = useDatos()
  const [aviso, setAviso] = useState(null)   // lo que «Terminar por hoy» dice al volver al inicio
  const sync = useSync()
  /* La puerta de entrada es el TRABAJO, no el archivo.
     Hasta el 2026-08-13 la aplicación abría en la lista de clientes, lo que obliga a
     recordar bajo qué nombre quedó guardada la casa antes de poder empezar. Juan pidió
     que abriera preguntando qué se va a hacer: una cotización nueva, o entrar a una
     que ya existe. La lista de clientes sigue estando, un toque más adentro. */
  const [vista, setVista] = useState({ p: 'inicio' })
  const [interno, setInterno] = useState(false)
  const [busqueda, setBusqueda] = useState('')
  const [carrito, setCarrito] = useState({})
  const [categoria, setCategoria] = useState(null)
  const [pestana, setPestana] = useState(0)
  const [captura, setCaptura] = useState(null)   // renglón de captura abierto, o null
  const [fotoDudosa, setFotoDudosa] = useState(null)
  const intervencionesCasa = useIntervencionesDeCasa(vista.casa)
  /* Las credenciales impresas son publicidad comprobable (LFPC art. 32): «Dictamen
     firmado» solo cuando esta casa tiene una Revisión con respuestas selladas. */
  const hayDictamenCasa = intervencionesCasa.some(iv => iv.tipo === 'diagnostico' && iv.protocoloId === PROTOCOLO_REVISION.id && (iv.respuestas || []).length > 0)
  const credencialesCasa = hayDictamenCasa ? 'Ingeniero titulado · Cédula profesional · Dictamen firmado' : 'Ingeniero titulado · Cédula profesional'

  /* Regla 3 de la teoría de diseño: cada cambio de vista RESETEA el scroll y entra
     con una transición direccional de 180 ms. Sin el reset, la vista nueva aparecía
     ya scrolleada donde quedó la anterior (corte seco medido en la crítica); la
     clase de animación se quita y se vuelve a poner para reiniciarla en el mismo
     nodo, porque React conserva el <main> entre vistas. */
  const claveVista = `${vista.p}|${vista.cli || ''}|${vista.casa || ''}|${vista.cot || ''}|${vista.hab || ''}`
  const profPrevia = useRef(-1)
  useEffect(() => {
    const prof = PROFUNDIDAD[vista.p] ?? 1
    const dir = prof >= profPrevia.current ? 'entra-avanza' : 'entra-vuelve'
    const esPrimera = profPrevia.current === -1
    profPrevia.current = prof
    window.scrollTo(0, 0)
    /* E3 — EL CURSOR DEL RECORRIDO: dentro de una cotización (plano, habitación o
       la propia cotización) cada cambio de vista deja escrito dónde quedó el
       vendedor. Si el perro, el cliente o iOS interrumpen, el inicio ofrece
       «Retomar: Casa Ruiz — Recámara 2». Es un documento chico en su estado
       personal; se borra con «Terminar por hoy». */
    if (vista.cot && ['plano', 'hab', 'cot'].includes(vista.p)) {
      const c = cotizaciones.find(x => x.id === vista.cot)
      const ca = casas.find(x => x.id === vista.casa)
      const h = c?.habitaciones?.find(x => x.id === vista.hab)
      store.guardar(`usuarios/${usuario()?.uid || 'local'}/estado`, {
        id: `recorrido-${vista.cot}`, p: vista.p, cli: vista.cli || null, casa: vista.casa || null,
        cot: vista.cot, hab: vista.hab || null, de: vista.de || null,
        nombreCasa: ca?.alias || c?.nombre || '', nombreRecinto: h?.nombre || '',
        actualizadoEn: new Date().toISOString()
      }).catch(() => {})
    }
    if (esPrimera) return          // el arranque no viene «de» ningún lado
    const m = document.querySelector('main')
    if (m) {
      m.classList.remove('entra-avanza', 'entra-vuelve')
      void m.offsetWidth
      m.classList.add(dir)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveVista])

  // El contexto se reconstruye cuando cambia el catálogo o los factores: editar un
  // costo o un factor recalcula todas las cotizaciones al instante.
  // Los conceptos especiales viven DENTRO de su cotización, en `item.def`, y nunca en
  // el catálogo permanente —que es lo que promete la pantalla que los crea—. Se
  // inyectan aquí para que el motor pueda resolverlos, y sobreviven a recargar la
  // página porque viajan en el documento de la cotización.
  const especiales = useMemo(
    () => cotizaciones.flatMap(c =>
      (c.habitaciones || []).flatMap(h => (h.items || []).map(i => i.def).filter(Boolean))),
    [cotizaciones]
  )

  const ctx = useMemo(
    () => contexto({
      productos: [...(productos.length ? productos : TODOS_PRODUCTOS), ...especiales],
      dispositivos: dispositivos.length ? dispositivos : DISPOSITIVOS,
      factores
    }),
    [productos, dispositivos, factores, especiales]
  )

  const cliente = clientes.find(c => c.id === vista.cli)
  const casa = casas.find(c => c.id === vista.casa)
  const cot = cotizaciones.find(c => c.id === vista.cot)
  const hab = cot?.habitaciones?.find(h => h.id === vista.hab)

  /* Deja constancia de que ESTE usuario abrió la cotización — es lo que alimenta
     «Lo último que tocaste» del inicio. Sin await en los clics: si la escritura
     tarda, la navegación no espera. */
  const registrarReciente = useCallback(async idCot => {
    const col = `usuarios/${usuario()?.uid || 'local'}/estado`
    const previo = await store.obtener(col, 'recientes').catch(() => null)
    const lista = [idCot, ...(previo?.lista || []).filter(x => x !== idCot)].slice(0, 8)
    await store.guardar(col, { id: 'recientes', lista })
  }, [])

  /** La única puerta de entrada a una cotización: navega Y la registra en recientes.
      `de` recuerda de dónde se entró, para que «Regresar» vuelva ahí (Juan,
      2026-08-22: los datos de la casa y del cliente son opción, no peaje). */
  const abrirCot = (x, de = 'cotizaciones') => {
    registrarReciente(x.id)
    setVista({ p: 'cot', cli: x.cliente, casa: x.casa, cot: x.id, de })
  }

  /* PARCHES, NO DOCUMENTOS COMPLETOS (E0, 2026-08-22). `guardarCot` recibe la
     cotización «siguiente» como siempre, pero escribe SOLO los campos de primer
     nivel que cambiaron respecto a la que está en pantalla: dos dispositivos
     editando campos distintos ya no se pisan (notas en uno, firma en otro).
     `habitaciones` sigue siendo un arreglo y se sustituye entero — es el límite
     conocido hasta que los objetos vivan en mapas por id (E1-E2). */
  const guardarCot = useCallback(async siguiente => {
    if (!cot || siguiente.id !== cot.id) { await store.guardar('cotizaciones', siguiente); return }
    const parche = {}
    for (const [k, v] of Object.entries(siguiente))
      if (JSON.stringify(v) !== JSON.stringify(cot[k])) parche[k] = v
    if (Object.keys(parche).length) await store.guardarParcial('cotizaciones', cot.id, parche)
  }, [cot])

  const mutarHab = useCallback(async cambio => {
    const copia = structuredClone(cot)
    const h = copia.habitaciones.find(x => x.id === vista.hab)
    cambio(h, copia)
    await guardarCot(copia)
  }, [cot, vista.hab, guardarCot])

  if (!listo || !rol) return <div className="app"><div className="empty">Cargando…</div></div>

  /** Crea la cotización y entra en ella. Un solo sitio, para que los caminos que
      llegan aquí —cliente nuevo, casa nueva, casa existente— no puedan divergir
      en los valores por defecto. La cotización cuelga de la CASA; `cliente` viaja
      también por compatibilidad y para las listas globales. */
  /* E1: una cotización nueva sobre una casa que ya tiene plano NACE con las
     habitaciones de sus recintos (mismo id); las que el llamador pida además
     (la de ámbito casa de un rubro no migrado) se suman sin duplicar. */
  const crearCotizacionId = async (idCliente, idCasa, nombre, habitaciones = []) => {
    const casaObj = casas.find(c => c.id === idCasa)
    const derivadas = habitacionesDesdePlano(normalizarPlano(casaObj?.plano))
    const ids = new Set(derivadas.map(h => h.id))
    const todas = [...derivadas, ...habitaciones.filter(h => !ids.has(h.id))]
    return store.guardar('cotizaciones', {
      cliente: idCliente, casa: idCasa, nombre, fecha: hoy(), equipo: equipos[0].id,
      distancia: equipos[0].distancia, minTraslado: equipos[0].tiempo, peaje: 0,
      uafir: 22, sobrePrecio: 0, aplicarUAFIR: true, estado: 'borrador',
      modo: 'etapas', etiquetas: {}, notasCot: '', habitaciones: todas
    })
  }
  const crearCotizacion = async (idCliente, idCasa, nombre) => {
    const id = await crearCotizacionId(idCliente, idCasa, nombre)
    registrarReciente(id)
    setVista({ p: 'cot', cli: idCliente, casa: idCasa, cot: id })
  }

  /* ---------- La visita: del rubro al motor sin pasar por ningún módulo ---------- */
  /* Entrar a un rubro es caer en su motor DENTRO de una cotización y una habitación
     que ya existen; `de: 'visita'` hace que el regreso y el guardado vuelvan a la
     cotización, no a la habitación. Smart home abre el catálogo en su categoría. */
  const entrarAlRubro = (rubro, ids) => {
    setBusqueda(''); setCaptura(null)
    if (rubro.p === 'add') { setCarrito({}); setCategoria(rubro.cat); setPestana(0) }
    setVista({ p: rubro.p, ...ids, de: 'visita' })
  }
  /** «Empezar» de ¿Para quién?: crea lo que falte —cliente, casa— más la cotización
      con la habitación del rubro ya dentro, y entra al motor. Un toque. */
  const empezarVisita = async ({ cli, nombre, casaSel, alias }, rubro) => {
    const idCliente = cli?.id || await store.guardar('clientes',
      { nombre, direccion: '', telefono: '', correo: '', nota: '' })
    const idCasa = casaSel?.id || await store.guardar('casas',
      { cliente: idCliente, alias, direccion: '', ubicacion: '', notasAcceso: '' })
    /* El orden real (Juan, 2026-08-22): primero para quién y en qué casa, y HASTA
       ENTONCES qué se va a cotizar. Sin rubro, se crea la cotización y se pasa a
       la pared de rubros ya dentro de la casa. Con rubro (atajo), se entra
       directo: para la capa eléctrica no se crea habitación por rubro (las
       habitaciones son los recintos); para los motores no migrados, la de ámbito
       casa. */
    const habId = store.nuevoId()
    const ambitoCasa = (!rubro || rubro.p === 'plano') ? [] : [{ id: habId, nombre: rubro.hab, ambito: 'casa', recinto: null, items: [] }]
    const idCot = await crearCotizacionId(idCliente, idCasa, casaSel?.alias || alias, ambitoCasa)
    registrarReciente(idCot)
    if (!rubro) { setVista({ p: 'rubros', cli: idCliente, casa: idCasa, cot: idCot, de: 'visita' }); return }
    entrarAlRubro(rubro, { cli: idCliente, casa: idCasa, cot: idCot, hab: ambitoCasa[0]?.id })
  }
  /** Navegación persistente: ir a una sección de primer nivel. */
  const ir = id => { setBusqueda(''); setCaptura(null); setAviso(null); setVista({ p: id }) }
  /** «Otro rubro en esta casa»: reutiliza la habitación de ámbito casa del rubro si
      ya existe; el plano no necesita ninguna. */
  const otroRubro = async rubro => {
    if (rubro.p === 'plano') { entrarAlRubro(rubro, { cli: cot.cliente, casa: cot.casa, cot: cot.id }); return }
    let h = (cot.habitaciones || []).find(x => x.nombre === rubro.hab)
    if (!h) {
      const copia = structuredClone(cot)
      h = { id: store.nuevoId(), nombre: rubro.hab, ambito: 'casa', recinto: null, items: [] }
      copia.habitaciones.push(h)
      await guardarCot(copia)
    }
    entrarAlRubro(rubro, { cli: cot.cliente, casa: cot.casa, cot: cot.id, hab: h.id })
  }
  /** La Revisión con instrumentos, desde el plano: la hace el ingeniero y guarda
      en la habitación de ámbito casa de lo eléctrico (su motor migra en E4). */
  const RUBRO_REVISION = { id: 'revision', p: 'electrico', hab: 'Instalación eléctrica' }

  /* E4b — LA CALCULADORA DE CAPA: lo propuesto en la paleta de un recinto se vuelve
     partidas de su habitación (una por SKU + la alimentación medida en el plano).
     Idempotente: sustituye solo lo de origen «paleta», respeta lo manual. */
  const partidasDe = recintoId => partidasDeRecinto({
    objetos: cot?.objetos, plano: normalizarPlano(casa?.plano), recintoId,
    rendimientos, minutosDia: factores.MinutosDia
  })
  /* Vocabulario de la paleta por rubro (F3): productos del catálogo vivo por categoría.
     Riego, Cámaras y Wi-Fi no tienen productos por pieza: abren su motor. */
  const productosPorRubro = (() => {
    const lista = productos.length ? productos : TODOS_PRODUCTOS
    const de = cats => lista.filter(p => cats.includes(p.cat) && !p.especial)
    return {
      smart: de(['aut', 'med']),
      iluminacion: de(['ilu']),
      taller: [...de(['arq']), ...PRODUCTOS_TALLER.filter(p => !lista.some(x => x.sku === p.sku))],
      /* E7-lite: equipos de los motores, colocados en el plano; su partida la calcula el motor. */
      riego: ASPERSORES.map(a => ({ sku: a.id, nombre: a.nombre })),
      camaras: CAMARAS.map(c => ({ sku: c.id, nombre: c.nombre })),
      wifi: PUNTOS_ACCESO.map(a => ({ sku: a.id, nombre: a.nombre }))
    }
  })()
  const calcularRecinto = async recintoId => {
    const { items: nuevas } = partidasDe(recintoId)
    const copia = structuredClone(cot)
    copia.habitaciones = copia.habitaciones.map(h => (h.recinto === recintoId || h.id === recintoId) ? aplicarPartidas(h, nuevas) : h)
    await guardarCot(copia)
  }
  const calcularRecorrido = async () => {
    const copia = structuredClone(cot)
    copia.habitaciones = copia.habitaciones.map(h => h.recinto ? aplicarPartidas(h, partidasDe(h.recinto).items) : h)
    await guardarCot(copia)
  }

  /** «Terminar por hoy» (E3): dice en llano si lo capturado ya subió —leyendo el
      estado real de la cola— y NUNCA bloquea; borra el cursor y vuelve al inicio. */
  const terminarPorHoy = async () => {
    const texto =
      sync.estado === 'sin-conexion' ? 'Sin señal ahora mismo: lo capturado está guardado en el teléfono y se sube solo en cuanto haya conexión. Puedes cerrar la app.'
      : sync.estado === 'pendiente' ? 'Quedan capturas subiendo: se terminan de subir solas, aunque cierres la app. No hace falta esperar.'
      : sync.estado === 'local' ? 'Todo quedó guardado en este equipo.'
      : 'Todo lo de hoy ya está subido.'
    setAviso(texto)
    if (vista.cot) await store.borrar(`usuarios/${usuario()?.uid || 'local'}/estado`, `recorrido-${vista.cot}`).catch(() => {})
    setVista({ p: 'inicio' })
  }
  const retomar = r => { setAviso(null); setVista({ p: r.p, cli: r.cli, casa: r.casa, cot: r.cot, hab: r.hab || undefined, de: r.de || undefined }) }

  /* ---------- Inicio: el verbo del día, por rol ---------- */
  /* Regla 1 de la teoría de diseño (aprobada 2026-08-20): el inicio PREGUNTA qué
     vienes a hacer, no lista lo que el sistema tiene. El vendedor abre en el
     levantamiento como puerta —la visita es el viaje que paga todo lo demás—; la
     cuadrilla abre en sus obras de hoy con la acción pendiente; el admin ve lo
     mismo que el vendedor más sus módulos. (El aliado nunca pasa por aquí: la
     compuerta de sesión lo manda directo a su Central.) */
  if (vista.p === 'inicio') {
    const MODULOS = [
      {
        id: 'cotizaciones', nombre: 'Cotizaciones',
        desc: 'Buscar o retomar una existente, por cliente, casa o nombre.',
        roles: ['admin', 'gerente', 'vendedor']
      },
      {
        id: 'clientes', nombre: 'Clientes',
        desc: 'La persona, sus casas, y las cotizaciones de cada casa.',
        roles: ['admin', 'gerente', 'vendedor']
      },
      {
        id: 'levantamientos', nombre: 'Levantamientos guiados',
        desc: 'Practicar o enseñar sin comprometer nada: eléctrico, riego, cámaras, Wi-Fi, iluminación y madera.',
        roles: ['admin', 'gerente', 'vendedor']
      },
      {
        id: 'encargos', nombre: 'Encargos',
        desc: 'La mesa de trabajo con el taller y los aliados, con sus revisiones.',
        roles: ['admin', 'gerente', 'vendedor']
      },
      {
        id: 'catalogo', nombre: 'Catálogo y productos',
        desc: 'Dar de alta productos, insumos, despieces y herramientas.',
        roles: ['admin']
      },
      {
        id: 'config', nombre: 'Configuración',
        desc: 'Factores del negocio, canales, criterios de obra y taller.',
        roles: ['admin']
      }
    ].filter(m => m.roles.includes(rol))
    const ultimas = recientes.map(id => cotizaciones.find(c => c.id === id)).filter(Boolean).slice(0, 3)
    return (
      <div className="app">
        <Barra titulo="Soham Central" migaja="Soham Home" acciones={<Sync s={sync} />} />
        <main className={rol !== 'cuadrilla' ? 'con-nav' : ''}>
          {rol === 'cuadrilla' ? (
            <InicioCuadrilla cotizaciones={cotizaciones} casas={casas}
              onBodega={cot => { registrarReciente(cot.id); setVista({ p: 'bodega', cli: cot.cliente, casa: cot.casa, cot: cot.id, de: 'inicio' }) }}
              onBitacora={cot => setVista({ p: 'bitacora', cli: cot.cliente, casa: cot.casa, de: 'inicio' })} />
          ) : (
            <>
              {/* LA PUERTA ES EL RUBRO (Teoría §1 v2): el vendedor llega por un motivo
                  concreto y toca por lo que lo llamaron. La visita frente al cliente es
                  el viaje que paga todo lo demás, y arranca aquí sin pasar por módulos. */}
              {aviso && <div className="nota acento">{aviso}</div>}
              {/* LA PUERTA (Juan, 2026-08-22): primero nueva o existente; si es nueva,
                  para quién y en qué casa; y hasta entonces qué se cotiza. La pared de
                  rubros vive ya dentro de la casa. */}
              <div className="acciones">
                <button type="button" className="accion eje"
                  onClick={() => { setAviso(null); setCaptura(null); setVista({ p: 'quien', de: 'inicio' }) }}>
                  <img src={FOTO_PORTADA} alt="" />
                  <b>Cotización nueva</b>
                  <span>Para quién y en qué casa; luego eliges qué cotizar y recorres la casa.</span>
                </button>
                <button type="button" className="accion" onClick={() => ir('cotizaciones')}>
                  <img className="patron" alt="" src={PATRON} />
                  <b>Abrir una cotización</b>
                  <span>Buscar por cliente, casa o nombre, o retomar una reciente.</span>
                </button>
              </div>
              {/* Lo que está pasando ahora mismo (Juan, 2026-08-22). */}
              <Resumen cotizaciones={cotizaciones} recorridos={recorridos} onIr={ir} onRetomar={retomar} />
              <Prospectos />
              {ultimas.length > 0 && (
                <>
                  <div className="sec">Lo último que tocaste</div>
                  {ultimas.map(x => (
                    <div className="card tap" key={x.id} {...tapProps(() => abrirCot(x))}>
                      <div className="grow">
                        <div className="t">{x.nombre}</div>
                        <div className="s">
                          {clientes.find(c => c.id === x.cliente)?.nombre || 'Cliente eliminado'}
                          {casas.find(c => c.id === x.casa) ? ` · ${casas.find(c => c.id === x.casa).alias}` : ''} · {x.fecha}
                        </div>
                      </div>
                      <div className="money">{mx(porCotizacion(x, ctx).total.total)}</div>
                      <div className="chev"><IconoAvanza /></div>
                    </div>
                  ))}
                </>
              )}
              <div className="sec">La plataforma</div>
              <div className="acciones">
                {MODULOS.map(m => (
                  <button type="button" key={m.id} className="accion"
                    onClick={() => { setBusqueda(''); setCaptura(null); setVista({ p: m.id }) }}>
                    <img className="patron" alt="" src={PATRON} />
                    <b>{m.nombre}</b>
                    <span>{m.desc}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          {/* Cerrar sesión al final, no en la esquina de arriba (Juan, 2026-08-20). */}
          <BotonSalir />
        </main>
        {rol !== 'cuadrilla' && <Nav rol={rol} activa="inicio" onIr={ir} />}
      </div>
    )
  }

  /* ---------- La visita: ¿para quién? ---------- */
  if (vista.p === 'quien') {
    const rubro = vista.rubro ? RUBROS.find(r => r.id === vista.rubro) || null : null
    return (
      <Quien key={rubro?.id || 'nueva'} rubro={rubro} clientes={clientes} casas={casas}
        atras={() => setVista({ p: vista.de === 'cotizaciones' ? 'cotizaciones' : 'inicio' })}
        onEmpezar={datos => empezarVisita(datos, rubro)} />
    )
  }

  /* ---------- El plano de la casa: el recorrido (E1) ---------- */
  if (vista.p === 'plano' && casa) {
    const planoV2 = normalizarPlano(casa.plano)
    return (
      <VistaPlano plano={planoV2} habitaciones={cot?.habitaciones || []}
        resumenDe={id => resumenObjetos(cot?.objetos, id)}
        titulo={casa.alias} migaja={cot ? cot.nombre : cliente?.nombre}
        Barra={Barra} Sync={<Sync s={sync} />}
        atras={() => setVista(cot ? { ...vista, p: 'cot' } : { p: 'casa', cli: vista.cli, casa: casa.id })}
        onParche={parche => store.guardarParcial('casas', casa.id,
          /* Sin `fusionar` aquí: borraría los centinelas de borrado (eliminar un recinto,
             deshacer un alta) antes de que lleguen al motor de datos. */
          { plano: { version: VERSION_PLANO, ...parche, plantas: { ...planoV2.plantas, ...(parche.plantas || {}) } } })}
        onRecintoNuevo={async r => {
          if (!cot || (cot.habitaciones || []).some(h => h.id === r.id)) return
          await guardarCot({ ...cot, habitaciones: [...(cot.habitaciones || []), { id: r.id, nombre: r.nombre, recinto: r.id, planta: r.planta, items: [] }] })
        }}
        onAbrirRecinto={id => {
          if (!cot) return
          const h = (cot.habitaciones || []).find(x => x.id === id || x.recinto === id)
          if (h) setVista({ ...vista, p: 'hab', hab: h.id, de: 'plano' })
        }}
        onRevision={cot ? () => otroRubro(RUBRO_REVISION) : undefined}
        onTerminar={cot ? terminarPorHoy : undefined}
        /* F3 — la paleta de todos los rubros en el plano, sobre la cotización abierta */
        cotId={cot?.id}
        objetos={cot?.objetos || {}}
        onObjeto={(id, valor) => cot && (valor === null
          ? store.borrarCampo('cotizaciones', cot.id, `objetos.${id}`)
          : store.guardarParcial('cotizaciones', cot.id, { objetos: { [id]: valor } }))}
        fotosDe={id => cot?.fotosRecinto?.[id] || []}
        onFotos={(id, lista) => cot && store.guardarParcial('cotizaciones', cot.id, { fotosRecinto: { [id]: lista } })}
        notaDe={id => cot?.notasRecinto?.[id] || ''}
        onNota={(id, texto) => cot && store.guardarParcial('cotizaciones', cot.id, { notasRecinto: { [id]: texto } })}
        productosPorRubro={productosPorRubro}
        onRecalcular={cot ? calcularRecinto : undefined}
        diagnostico={cot?.diagnostico || {}}
        onDiagnostico={parche => cot && store.guardarParcial('cotizaciones', cot.id, { diagnostico: parche })}
        onAbrirMotor={id => { const r = RUBROS.find(x => x.id === id); if (r && cot) otroRubro(r) }} />
    )
  }

  /* ---------- La visita: qué se cotiza en esta casa ---------- */
  if (vista.p === 'rubros' && cot) {
    const hayPartidas = (cot.habitaciones || []).some(h => (h.items || []).length)
    return (
      <div className="app">
        <Barra titulo={hayPartidas ? '¿Qué más va en esta casa?' : '¿Qué vas a cotizar?'}
          migaja={`${casa?.alias || cot.nombre} · ${cliente?.nombre || ''}`}
          atras={() => setVista({ ...vista, p: 'cot' })} />
        <main>
          <ParedDeRubros onElegir={otroRubro} />
          {!hayPartidas && (
            <div className="s" style={{ marginTop: 'var(--e5)' }}>
              Toca por lo que te llamaron. Después podrás agregar otros rubros en la misma casa.
            </div>
          )}
        </main>
      </div>
    )
  }

  /* ---------- Módulo Cotizaciones: nueva, recientes personales y buscador ---------- */
  /* «Lo último que tocaste» es literal y PERSONAL: las cotizaciones que este
     usuario abrió, en su orden. Nadie ve los recientes de otro. */
  if (vista.p === 'cotizaciones') {
    const t = busqueda.toLowerCase()
    const resultados = t
      ? cotizaciones
          .map(x => ({ ...x, cli: clientes.find(c => c.id === x.cliente), casaDe: casas.find(c => c.id === x.casa) }))
          .filter(x => [x.nombre, x.cli?.nombre, x.casaDe?.alias, x.casaDe?.direccion].join(' ').toLowerCase().includes(t))
          .sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''))
      : []
    /* Buscar por CLIENTE (Juan, 2026-08-22): el cliente aparece primero; si tiene una
       sola casa se entra directo a ella, si tiene varias se eligen aquí mismo. */
    const clientesHallados = t
      ? clientes.filter(c => [c.nombre, c.telefono, c.correo].join(' ').toLowerCase().includes(t)).slice(0, 5)
      : []
    const entrarACliente = c => {
      const suyas = casas.filter(x => x.cliente === c.id)
      if (suyas.length === 1) { setCaptura(null); setVista({ p: 'casa', cli: c.id, casa: suyas[0].id, de: 'cotizaciones' }) }
      else if (suyas.length === 0) setVista({ p: 'cliente', cli: c.id })
      else setCaptura({ tipo: 'elegir-casa', cli: c.id })
    }
    const ultimas = recientes.map(id => cotizaciones.find(c => c.id === id)).filter(Boolean)
    const tarjeta = x => (
      <div className="card tap" key={x.id} {...tapProps(() => abrirCot(x))}>
        <div className="grow">
          <div className="t">{x.nombre}</div>
          <div className="s">
            {clientes.find(c => c.id === x.cliente)?.nombre || 'Cliente eliminado'}
            {casas.find(c => c.id === x.casa) ? ` · ${casas.find(c => c.id === x.casa).alias}` : ''} · {x.fecha}
          </div>
        </div>
        <div className="money">{mx(porCotizacion(x, ctx).total.total)}</div>
        <div className="chev"><IconoAvanza /></div>
      </div>
    )
    return (
      <div className="app">
        <Barra titulo="Cotizaciones" migaja="Soham Central"
          atras={() => setVista({ p: 'inicio' })} acciones={<Sync s={sync} />} />
        <Nav rol={rol} activa="cotizaciones" onIr={ir} />
        <main className="con-nav">
          <div className="acciones">
            <button type="button" className="accion eje"
              onClick={() => { setCaptura(null); setVista({ p: 'quien', de: 'cotizaciones' }) }}>
              <img src={FOTO_PORTADA} alt="" />
              <b>Cotización nueva</b>
              <span>Se levanta la medida, se arma el alcance por habitación y sale el
                    documento firmado el mismo día.</span>
            </button>
          </div>
          <Buscador valor={busqueda} onCambio={setBusqueda}
            etiqueta="Buscar por cotización, cliente, casa o dirección" />
          {t ? (
            <>
              {clientesHallados.map(c => {
                const suyas = casas.filter(x => x.cliente === c.id)
                const eligiendo = captura?.tipo === 'elegir-casa' && captura.cli === c.id
                return (
                  <div key={c.id}>
                    <div className="card tap" {...tapProps(() => entrarACliente(c))}>
                      <div className="grow">
                        <div className="t">{c.nombre}</div>
                        <div className="s">{plural(suyas.length, 'casa', 'casas')} · {plural(cotizaciones.filter(x => x.cliente === c.id).length, 'cotización', 'cotizaciones')}</div>
                      </div>
                      <div className="chev"><IconoAvanza /></div>
                    </div>
                    {eligiendo && (
                      <div className="fila-pills" style={{ margin: '0 0 var(--e5) var(--e6)' }}>
                        {suyas.map(x => (
                          <button type="button" key={x.id} className="ib sm"
                            onClick={() => { setCaptura(null); setVista({ p: 'casa', cli: c.id, casa: x.id, de: 'cotizaciones' }) }}>{x.alias}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
              {resultados.length > 0 && clientesHallados.length > 0 && <div className="sec">Cotizaciones</div>}
              {resultados.map(tarjeta)}
              {!resultados.length && !clientesHallados.length && (
                <div className="empty">
                  <b>Nada coincide con «{busqueda}».</b>
                  <p>Se busca por cliente (nombre, teléfono, correo), por cotización, y por alias o
                     dirección de la casa.</p>
                </div>
              )}
            </>
          ) : ultimas.length ? (
            <>
              <div className="sec">Lo último que tocaste</div>
              {ultimas.map(tarjeta)}
            </>
          ) : cotizaciones.length ? (
            <div className="empty">
              <b>Todavía no abres ninguna cotización desde esta cuenta.</b>
              <p>Busca arriba por nombre, cliente o casa — al abrir una, se queda aquí
                 como reciente.</p>
            </div>
          ) : (
            <div className="empty">
              <b>Todavía no hay cotizaciones.</b>
              <p>Toca «Cotización nueva» para levantar la primera.</p>
            </div>
          )}
        </main>
      </div>
    )
  }

  /* ---------- Módulo Levantamientos guiados ---------- */
  if (vista.p === 'levantamientos') {
    return (
      <div className="app">
        <Barra titulo="Levantamientos guiados" migaja="Soham Central"
          atras={() => setVista({ p: 'inicio' })} />
        <Nav rol={rol} activa="levantamientos" onIr={ir} />
        <main className="con-nav">
          {/* PRIMERA REBANADA DE LA TESIS DEL 2026-08-13. Son levantamientos sueltos:
              se pueden correr en el jardín antes de que exista un cliente, para
              practicar o para enseñar sin comprometer nada. Dentro de una habitación,
              estos mismos motores sí guardan a la cotización. */}
          <div className="acciones">
            <button type="button" className="accion eje"
              onClick={() => setVista({ p: 'electrico' })}>
              <img src={FOTO_PORTADA} alt="" />
              <b>Revisión eléctrica</b>
              <span>Cuentas los aparatos y mides; el sistema saca los circuitos, el balanceo
                    entre fases, el calibre de cada cable y por qué ese y no otro.</span>
            </button>
            <button type="button" className="accion"
              onClick={() => setVista({ p: 'riego' })}>
              <img className="patron" alt="" src={PATRON} />
              <b>Riego de jardín</b>
              <span>Trazas el jardín, colocas los aspersores y calcula sola la tubería, las
                    zonas y la bomba.</span>
            </button>
            <button type="button" className="accion"
              onClick={() => setVista({ p: 'camaras' })}>
              <img className="patron" alt="" src={PATRON} />
              <b>Videovigilancia</b>
              <span>Trazas el predio, colocas y apuntas las cámaras, y te dice dónde vas a
                    identificar una cara y dónde sólo a saber que alguien pasó.</span>
            </button>
            <button type="button" className="accion"
              onClick={() => setVista({ p: 'wifi' })}>
              <img className="patron" alt="" src={PATRON} />
              <b>Wi-Fi y conectividad</b>
              <span>Cuántos puntos de acceso pide la casa por cobertura y por cantidad de
                    aparatos, y dónde hay que ir a medir.</span>
            </button>
            <button type="button" className="accion"
              onClick={() => setVista({ p: 'iluminacion' })}>
              <img className="patron" alt="" src={PATRON} />
              <b>Iluminación y control</b>
              <span>Cuántas luminarias pide cada cuarto según lo que ahí se hace, y con qué
                    se controla. Dice cuándo el nivel lo exige la ley y cuándo no.</span>
            </button>
            <button type="button" className="accion"
              onClick={() => setVista({ p: 'taller' })}>
              <img className="patron" alt="" src={PATRON} />
              <b>Fabricación en madera</b>
              <span>Mides el hueco, repartes los módulos, y salen las placas, los herrajes
                    y los minutos de máquina. Con el precio por metro que el cliente compara.</span>
            </button>
          </div>
        </main>
      </div>
    )
  }

  /* ---------- Módulo Encargos: todos, sin entrar cotización por cotización ---------- */
  if (vista.p === 'encargos') {
    return (
      <div className="app">
        <Barra titulo="Encargos" migaja="Soham Central"
          atras={() => setVista({ p: 'inicio' })} acciones={<Sync s={sync} />} />
        <Nav rol={rol} activa="encargos" onIr={ir} />
        <main className="con-nav">
          <ListaEncargos cotizaciones={cotizaciones}
            onAbrir={(e, c) => setVista({ p: 'encargo-taller', cli: c.cliente, casa: c.casa, cot: c.id, de: 'encargos' })} />
        </main>
      </div>
    )
  }

  /* ---------- Módulo Catálogo: alta y edición de lo que se vende ---------- */
  if (vista.p === 'catalogo') return (
    <div className="app">
      <Barra titulo="Catálogo y productos" migaja="Soham Central"
        atras={() => setVista({ p: 'inicio' })} />
      <Nav rol={rol} activa="catalogo" onIr={ir} />
      <main className="con-nav">
        <Configuracion grupo="catalogo" factores={factores} preferencias={preferencias} taller={taller}
          productos={productos.length ? productos : TODOS_PRODUCTOS}
          despieces={despieces}
          onGuardarDespiece={(sku, d) => store.guardar('despieces', { ...d, id: sku })}
          dispositivos={dispositivos.length ? dispositivos : DISPOSITIVOS}
          herramientas={herramientas} protocolos={protocolos}
          equipos={equipos} categorias={TODAS_CATEGORIAS} ctx={ctx} />
      </main>
    </div>
  )

  /* ---------- Levantamientos guiados (los motores) ---------- */
  /* Los levantamientos reciben el catálogo VIVO y las preferencias de obra: eligen el
     material por física y lo cobran con lo que Juan tenga dado de alta hoy. Si él sube el
     precio del cable esta tarde, el levantamiento de mañana ya sale con el nuevo. */
  /* Un levantamiento se puede abrir de dos maneras, y la diferencia importa:
       · SUELTO, desde el inicio — para practicar o para enseñarle algo a un cliente sin
         comprometer nada. No tiene a dónde guardarse, así que no ofrece guardar.
       · DENTRO de una habitación — ahí sí sabe a qué cotización pertenece, y el botón de
         «Agregar a la cotización» aparece.
     `vista.hab` es lo que distingue los dos casos. */
  if (vista.p === 'riego' || vista.p === 'electrico' || vista.p === 'camaras' ||
      vista.p === 'wifi' || vista.p === 'iluminacion' ||
      vista.p === 'taller') {
    const dentroDeHabitacion = !!(cot && hab)
    /* En la visita (de: 'visita') se vuelve a la COTIZACIÓN, que es donde vive el
       total y las tres salidas; desde una habitación se vuelve a la habitación. */
    const regreso = vista.de === 'visita' ? 'cot' : 'hab'
    const volver = dentroDeHabitacion
      ? () => setVista({ ...vista, p: regreso })
      : () => setVista({ p: 'levantamientos' })
    /* ESCALÓN 9a — el plano vive en la casa: el jardín (riego) y el predio
       (cámaras) se leen de `casa.plano[rubro]` y se devuelven ahí al cerrar el
       trazo. El siguiente rubro, o la siguiente visita, lo encuentra hecho. */
    /* E0: una sola verdad. Se LEE la versión 2 (una casa con el bloque viejo de
       9a se normaliza al vuelo) y se ESCRIBE versión 2 como parche de recintos y
       servicios — el bloque viejo del mismo motor se borra en esa misma escritura. */
    const clavePlano = vista.p
    const plano = casa ? planoParaMotor(normalizarPlano(casa.plano), clavePlano) : null
    const onPlano = casa && RECINTO_DEL_MOTOR[clavePlano]
      ? datos => store.guardarParcial('casas', casa.id, parcheDesdeMotor(clavePlano, datos))
      : undefined

    const guardar = dentroDeHabitacion
      ? async r => {
          const comunes = { rendimientos, minutosDia: factores.MinutosDia }
          const esp =
            vista.p === 'riego' ? riegoAProducto(r, { ...comunes, nombre: `Riego — ${hab.nombre}` })
            : vista.p === 'camaras' ? camarasAProducto(r, { ...comunes, nombre: `Videovigilancia — ${hab.nombre}` })
            : vista.p === 'wifi' ? wifiAProducto(r, { ...comunes, nombre: `Wi-Fi — ${hab.nombre}` })
            : vista.p === 'iluminacion' ? iluminacionAProducto(r, { ...comunes, nombre: `Iluminación — ${hab.nombre}` })
            : vista.p === 'taller' ? tallerAProducto(r, { ...comunes, factores, nombre: `Fabricación — ${hab.nombre}` })
            : electricoAProducto(r, { ...comunes, nombre: `Instalación eléctrica — ${hab.nombre}` })
          await mutarHab(h => h.items.push({
            sku: esp.sku, q: 1, e1: true, e2: false, e3: false, fotos: [], def: esp
          }))
          setVista({ ...vista, p: regreso })
        }
      : undefined

    const comun = { atras: volver, onGuardar: guardar, insumos: dispositivos, preferencias, plano, onPlano, semilla: semillaDe(vista.p, cot?.objetos) }
    if (vista.p === 'riego') return <Riego {...comun} />
    if (vista.p === 'camaras') return <Camaras {...comun} />
    if (vista.p === 'wifi') return <Wifi {...comun} />
    if (vista.p === 'iluminacion') return <Iluminacion {...comun} />
    /* El taller no usa el catálogo de insumos: su costo sale del despiece contra la
       configuración del taller. Por eso recibe `taller` y no `insumos`. */
    if (vista.p === 'taller') return <Taller atras={volver} onGuardar={guardar} taller={taller} />
    return <Electrico {...comun} />
  }

  /* ---------- Cotización nueva: ¿para quién? ---------- */
  if (vista.p === 'nueva') return (
    <div className="app">
      <Barra titulo="¿Para quién es?" migaja="Cotización nueva"
        atras={() => setVista({ p: vista.de === 'inicio' ? 'inicio' : 'cotizaciones' })} />
      <main>
        <div className="acciones">
          <button type="button" className="accion"
            onClick={() => { setBusqueda(''); setVista({ p: 'nueva-existente', de: vista.de }) }}>
            <img className="patron" alt="" src={PATRON} />
            <b>Un cliente que ya tengo</b>
            <span>{plural(clientes.length, 'cliente registrado', 'clientes registrados')}.
                  Se le agrega otra cotización.</span>
          </button>
          <button type="button" className="accion eje"
            onClick={() => setVista({ p: 'nueva-cliente', de: vista.de })}>
            <img src={FOTO_PORTADA} alt="" />
            <b>Un cliente nuevo</b>
            <span>Primero se registra —con el nombre basta— y de ahí sale directo a su
                  primera cotización.</span>
          </button>
        </div>
      </main>
    </div>
  )

  /* ---------- Cotización nueva: elegir cliente existente ---------- */
  if (vista.p === 'nueva-existente') {
    const t = busqueda.toLowerCase()
    const lista = clientes.filter(c =>
      !t || [c.nombre, c.direccion, c.telefono, c.correo].join(' ').toLowerCase().includes(t))
    return (
      <div className="app">
        <Barra titulo="¿De quién es la casa?" migaja="Cotización nueva"
          atras={() => setVista({ p: 'nueva', de: vista.de })} />
        <main>
          <Buscador valor={busqueda} onCambio={setBusqueda}
            etiqueta="Buscar por nombre, dirección, correo o teléfono" />
          {lista.length ? lista.map(c => (
            <div className="card tap" key={c.id}
              {...tapProps(() => { setCaptura(null); setVista({ p: 'nueva-casa', cli: c.id, de: vista.de }) })}>
              <div className="grow">
                <div className="t">{c.nombre}</div>
                <div className="s">{plural(casas.filter(x => x.cliente === c.id).length, 'casa registrada', 'casas registradas')}</div>
              </div>
              <div className="s">
                {plural(cotizaciones.filter(x => x.cliente === c.id).length, 'cotización', 'cotizaciones')}
              </div>
              <div className="chev"><IconoAvanza /></div>
            </div>
          )) : (
            <div className="empty">
              <b>Ningún cliente coincide con «{busqueda}».</b>
              <p>Puedes registrarlo como cliente nuevo desde la pantalla anterior.</p>
            </div>
          )}
        </main>
      </div>
    )
  }

  /* ---------- Cotización nueva: ¿en qué casa? ---------- */
  /* La casa es la entidad nueva del modelo (2026-08-18): un cliente tendrá varias
     —la de Satélite, la de descanso— y cada cotización cuelga de UNA. Elegirla es
     el paso que antes no existía y que evita que las cotizaciones de dos casas del
     mismo cliente se revuelvan. */
  if (vista.p === 'nueva-casa') {
    const cli = clientes.find(c => c.id === vista.cli)
    const lista = casas.filter(x => x.cliente === vista.cli)
    return (
      <div className="app">
        <Barra titulo="¿En qué casa?" migaja={cli?.nombre || 'Cotización nueva'}
          atras={() => { setCaptura(null); setVista({ p: 'nueva-existente', de: vista.de }) }} />
        <main>
          {captura?.tipo === 'casa' && (
            <Captura key="casa" etiqueta="Alias de la casa (p. ej. «Casa Satélite»)" accion="Continuar"
              inicial={lista.length ? '' : nombrePropuesto(cli?.nombre)}
              onCancelar={() => setCaptura(null)}
              onConfirmar={async alias => {
                const id = await store.guardar('casas',
                  { cliente: vista.cli, alias, direccion: '', ubicacion: '', notasAcceso: '' })
                setCaptura({ tipo: 'cotizacion', casa: id, alias })
              }} />
          )}
          {captura?.tipo === 'cotizacion' && (
            <Captura key="cot" etiqueta="Nombre de la cotización" accion="Abrir"
              inicial={captura.alias || ''}
              onCancelar={() => setCaptura(null)}
              onConfirmar={async n => { setCaptura(null); await crearCotizacion(vista.cli, captura.casa, n) }} />
          )}
          {lista.map(x => (
            <div className="card tap" key={x.id}
              {...tapProps(() => setCaptura({ tipo: 'cotizacion', casa: x.id, alias: x.alias }))}>
              <div className="grow">
                <div className="t">{x.alias}</div>
                <div className="s">{x.direccion || 'Sin dirección'}</div>
              </div>
              <div className="s">
                {plural(cotizaciones.filter(c => c.casa === x.id).length, 'cotización', 'cotizaciones')}
              </div>
              <div className="chev"><IconoAvanza /></div>
            </div>
          ))}
          {!captura && (
            <button type="button" className="discreto" onClick={() => setCaptura({ tipo: 'casa' })}>
              {lista.length ? 'Es otra casa — registrarla' : 'Registrar su primera casa'}
            </button>
          )}
        </main>
      </div>
    )
  }

  /* ---------- Cotización nueva: registrar cliente y seguir ---------- */
  if (vista.p === 'nueva-cliente') return (
    <div className="app">
      <Barra titulo="Cliente nuevo" migaja="Cotización nueva"
        atras={() => setVista(clientes.length
          ? { p: 'nueva', de: vista.de }
          : { p: vista.de === 'inicio' ? 'inicio' : 'cotizaciones' })} />
      <main>
        {/* Tres pasos encadenados: se registra el cliente, luego su casa, y sin
            volver atrás se le abre su primera cotización. Volver a la lista en
            medio rompe el hilo.

            El `key` distinto en cada paso NO es decorativo: `Captura` inicializa su
            campo con `useState(inicial)`, que solo corre al montar. Sin `key`, React
            reutiliza la misma instancia entre pasos y el campo siguiente aparecería
            con el valor del paso anterior. */}
        {/* Cada paso se anuncia como «paso N de 3» (crítica 2026-08-20): el título de
            la barra no cambia entre los tres y el vendedor no sabía cuánto faltaba. */}
        {captura?.tipo === 'cotizacion' ? (
          <Captura key="cot" etiqueta="Paso 3 de 3 — nombre de la cotización" accion="Abrir"
            inicial={captura.alias || ''}
            onCancelar={() => setCaptura(null)}
            onConfirmar={async n => { setCaptura(null); await crearCotizacion(captura.cli, captura.casa, n) }} />
        ) : captura?.tipo === 'casa' ? (
          <Captura key="casa" etiqueta="Paso 2 de 3 — alias de la casa (p. ej. «Casa Satélite»)" accion="Continuar"
            inicial={nombrePropuesto(captura.nombreCliente)}
            onCancelar={() => setCaptura(null)}
            onConfirmar={async alias => {
              const id = await store.guardar('casas',
                { cliente: captura.cli, alias, direccion: '', ubicacion: '', notasAcceso: '' })
              setCaptura({ tipo: 'cotizacion', cli: captura.cli, casa: id, alias })
            }} />
        ) : (
          <Captura key="cli" etiqueta="Paso 1 de 3 — nombre del cliente" accion="Continuar"
            onCancelar={() => setVista(clientes.length
              ? { p: 'nueva', de: vista.de }
              : { p: vista.de === 'inicio' ? 'inicio' : 'cotizaciones' })}
            onConfirmar={async n => {
              const id = await store.guardar('clientes',
                { nombre: n, direccion: '', telefono: '', correo: '', nota: '' })
              setCaptura({ tipo: 'casa', cli: id, nombreCliente: n })
            }} />
        )}
      </main>
    </div>
  )

  /* ---------- Clientes ---------- */
  if (vista.p === 'clientes') {
    const t = busqueda.toLowerCase()
    const lista = clientes.filter(c =>
      !t || [c.nombre, c.direccion, c.telefono, c.correo].join(' ').toLowerCase().includes(t))
    return (
      <div className="app">
        <Barra titulo="Clientes" migaja="Registro"
          atras={() => setVista({ p: 'inicio' })}
          acciones={<>
            <Sync s={sync} />
            <button className="ib sm pri ico" aria-label="Nuevo cliente"
              onClick={() => setCaptura({ tipo: 'cliente' })}><IconoMas /></button>
          </>} />
        <Nav rol={rol} activa="clientes" onIr={ir} />
        <main className="con-nav">
          <Buscador valor={busqueda} onCambio={setBusqueda}
            etiqueta="Buscar por nombre, dirección, correo o teléfono" />
          {captura?.tipo === 'cliente' && (
            <Captura etiqueta="Nombre del cliente" onCancelar={() => setCaptura(null)}
              onConfirmar={async n => {
                setCaptura(null)
                await store.guardar('clientes',
                  { nombre: n, direccion: '', telefono: '', correo: '', nota: '' })
              }} />
          )}
          {lista.length ? lista.map(c => (
            <div className="card tap" key={c.id}
              {...tapProps(() => { setVista({ p: 'cliente', cli: c.id }); setBusqueda(''); setCaptura(null) })}>
              <div className="grow">
                <div className="t">{c.nombre}</div>
                <div className="s">{plural(casas.filter(x => x.cliente === c.id).length, 'casa', 'casas')}</div>
              </div>
              <div className="s">
                {plural(cotizaciones.filter(x => x.cliente === c.id).length, 'cotización', 'cotizaciones')}
              </div>
              <div className="chev"><IconoAvanza /></div>
            </div>
          )) : clientes.length ? (
            <div className="empty"><b>Ningún cliente coincide con «{busqueda}».</b><p>Se busca por nombre, dirección, correo y teléfono.</p></div>
          ) : (
            /* Primer arranque. No es el mismo caso que una búsqueda sin resultados, y
               decírselo igual deja a quien abre la herramienta sin saber qué hacer. */
            <div className="portada">
              <img src={FOTO_PORTADA} alt="" />
              <div className="portada-txt">
                <b>Todavía no hay clientes.</b>
                <p>Cada cotización cuelga de un cliente, así que ese es el primer paso.
                   Con el nombre basta; la dirección y el teléfono se llenan después.</p>
                <button className="ib pri" onClick={() => setCaptura({ tipo: 'cliente' })}>
                  Registrar el primer cliente
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    )
  }

  /* ---------- Configuración: los supuestos del negocio ---------- */
  /* El catálogo ya no vive aquí: es su propio módulo. Aquí quedan los factores,
     los canales, los criterios de obra y el taller — lo que mueve precios. */
  if (vista.p === 'config') return (
    <div className="app">
      <Barra titulo="Configuración" migaja="Los supuestos del negocio"
        atras={() => setVista({ p: 'inicio' })} />
      <Nav rol={rol} activa="config" onIr={ir} />
      <main className="con-nav">
        <Configuracion grupo="negocio" factores={factores} preferencias={preferencias} taller={taller} productos={productos.length ? productos : TODOS_PRODUCTOS}
          despieces={despieces}
          onGuardarDespiece={(sku, d) => store.guardar('despieces', { ...d, id: sku })}
          dispositivos={dispositivos.length ? dispositivos : DISPOSITIVOS}
          herramientas={herramientas} protocolos={protocolos}
          equipos={equipos} categorias={TODAS_CATEGORIAS} ctx={ctx} />
      </main>
    </div>
  )

  /* ---------- El cliente: sus datos y sus casas ---------- */
  /* El cliente es LA PERSONA (nombre, teléfono, correo, notas); la dirección ya no
     vive aquí sino en cada casa. Sus cotizaciones se llega a ellas por la casa. */
  if (vista.p === 'cliente') {
    const susCasas = casas.filter(x => x.cliente === cliente?.id)
    const susCots = cotizaciones.filter(x => x.cliente === cliente?.id)
    const guardarCliente = campos => store.guardar('clientes', { ...cliente, ...campos })
    return (
      <div className="app">
        <Barra titulo={cliente?.nombre || ''} migaja="Cliente"
          atras={() => setVista(
            vista.de === 'cot' && vista.cot ? { p: 'cot', cli: vista.cli, casa: vista.casa, cot: vista.cot, de: vista.origen }
            : { p: 'clientes' })}
          acciones={<>
            <Sync s={sync} />
            <button className="ib sm pri ico" aria-label="Casa nueva"
              onClick={() => setCaptura({ tipo: 'casa' })}><IconoMas /></button>
          </>} />
        <main>
          <div className="sec">La persona</div>
          <div className="card"><div className="grow">
            <CampoGuardado etiqueta="Nombre completo" valor={cliente?.nombre}
              onGuardar={v => v && guardarCliente({ nombre: v })} />
            <CampoGuardado etiqueta="Teléfono" tipo="tel" valor={cliente?.telefono}
              onGuardar={v => guardarCliente({ telefono: v })} />
            <CampoGuardado etiqueta="Correo" tipo="email" valor={cliente?.correo}
              onGuardar={v => guardarCliente({ correo: v })} />
            <CampoGuardado etiqueta="Notas" multilinea valor={cliente?.nota}
              onGuardar={v => guardarCliente({ nota: v })} />
          </div></div>

          <div className="sec">Sus casas</div>
          {captura?.tipo === 'casa' && (
            <Captura etiqueta="Alias de la casa (p. ej. «Casa Satélite»)"
              inicial={susCasas.length ? '' : nombrePropuesto(cliente?.nombre)}
              onCancelar={() => setCaptura(null)}
              onConfirmar={async alias => {
                setCaptura(null)
                const id = await store.guardar('casas',
                  { cliente: cliente.id, alias, direccion: '', ubicacion: '', notasAcceso: '' })
                setVista({ p: 'casa', cli: cliente.id, casa: id })
              }} />
          )}
          {susCasas.length ? susCasas.map(x => (
            <div className="card tap" key={x.id}
              {...tapProps(() => { setCaptura(null); setVista({ p: 'casa', cli: cliente.id, casa: x.id }) })}>
              <div className="grow">
                <div className="t">{x.alias}</div>
                <div className="s">{x.direccion || 'Sin dirección'}</div>
              </div>
              <div className="s">
                {plural(cotizaciones.filter(c => c.casa === x.id).length, 'cotización', 'cotizaciones')}
              </div>
              <div className="chev"><IconoAvanza /></div>
            </div>
          )) : !captura && (
            <div className="empty">
              <b>Este cliente no tiene casas registradas.</b>
              <p>La casa es donde se trabaja: cada cotización cuelga de una. Con el alias basta;
                 la dirección y las notas de acceso se llenan después.</p>
            </div>
          )}
        </main>
        <Cuadro
          celdas={[
            { k: 'Casas', v: susCasas.length },
            { k: 'Cotizaciones', v: susCots.length }
          ]}
          importe={susCots.reduce((a, x) => a + porCotizacion(x, ctx).total.total, 0)}
          rotuloImporte="Suma cotizada" />
      </div>
    )
  }

  /* ---------- La casa: su ficha y sus cotizaciones ---------- */
  if (vista.p === 'casa') {
    const lista = cotizaciones.filter(x => x.casa === casa?.id)
    const guardarCasa = campos => store.guardar('casas', { ...casa, ...campos })
    return (
      <div className="app">
        <Barra titulo={casa?.alias || ''} migaja={cliente?.nombre}
          atras={() => setVista(
            vista.de === 'cot' && vista.cot ? { p: 'cot', cli: vista.cli, casa: casa.id, cot: vista.cot, de: vista.origen }
            : vista.de === 'cotizaciones' ? { p: 'cotizaciones' }
            : { p: 'cliente', cli: vista.cli })}
          acciones={<>
            <Sync s={sync} />
            <button type="button" className="ib pri ico" aria-label="Nueva cotización"
              onClick={() => setCaptura({ tipo: 'cotizacion' })}><IconoMas /></button>
          </>} />
        <main>
          <div className="sec">La casa</div>
          <div className="card"><div className="grow">
            <CampoGuardado etiqueta="Alias" valor={casa?.alias}
              onGuardar={v => v && guardarCasa({ alias: v })} />
            <CampoGuardado etiqueta="Dirección" valor={casa?.direccion}
              onGuardar={v => guardarCasa({ direccion: v })} />
            <CampoGuardado etiqueta="Ubicación (liga de Google Maps)" valor={casa?.ubicacion}
              onGuardar={v => guardarCasa({ ubicacion: v })} />
            <CampoGuardado etiqueta="Notas de acceso (portón, caseta, horarios…)" multilinea
              valor={casa?.notasAcceso} onGuardar={v => guardarCasa({ notasAcceso: v })} />
          </div></div>
          {!!casa?.ubicacion && (
            <button type="button" className="discreto"
              onClick={() => window.open(casa.ubicacion, '_blank', 'noopener')}>
              Abrir la ubicación en el mapa
            </button>
          )}

          {/* El plano es de la CASA (E1): sobrevive a las visitas y lo usan todos
              los rubros. Desde aquí se dibuja o se corrige sin cotización abierta. */}
          <div className="card tap" {...tapProps(() => setVista({ p: 'plano', cli: vista.cli, casa: casa.id }))}>
            <div className="grow">
              <div className="t">Plano de la casa</div>
              <div className="s">
                {Object.keys(normalizarPlano(casa?.plano).recintos).length
                  ? plural(Object.keys(normalizarPlano(casa?.plano).recintos).length, 'recinto dibujado', 'recintos dibujados')
                  : 'Todavía sin dibujar: se traza en la primera visita.'}
              </div>
            </div>
            <div className="chev"><IconoAvanza /></div>
          </div>

          {/* La bitácora es de la CASA, no de una cotización: acumula intervenciones
              y garantías de todos los trabajos que se le hayan hecho. */}
          <div className="card tap" {...tapProps(() => setVista({ p: 'bitacora', cli: vista.cli, casa: casa.id }))}>
            <div className="grow">
              <div className="t">Bitácora de la casa</div>
              <div className="s">Intervenciones, garantías y a qué venta es achacable cada cosa.</div>
            </div>
            <div className="chev"><IconoAvanza /></div>
          </div>

          <div className="sec">Cotizaciones de esta casa</div>
          {captura?.tipo === 'cotizacion' && (
            <Captura etiqueta="Nombre de la cotización" accion="Abrir"
              inicial={casa?.alias || ''}
              onCancelar={() => setCaptura(null)}
              onConfirmar={async n => { setCaptura(null); await crearCotizacion(casa.cliente, casa.id, n) }} />
          )}
          {lista.length ? lista.map(x => (
            <div className="card tap" key={x.id} {...tapProps(() => abrirCot(x, 'casa'))}>
              <div className="grow">
                <div className="t">{x.nombre}</div>
                <div className="s">
                  {x.fecha} · {plural((x.habitaciones || []).length, 'habitación', 'habitaciones')}
                </div>
              </div>
              <div className="money">{mx(porCotizacion(x, ctx).total.total)}</div>
              <div className="chev"><IconoAvanza /></div>
            </div>
          )) : !captura && (
            <div className="empty"><b>Esta casa no tiene cotizaciones.</b><p>Toca el botón de arriba para abrirle la primera.</p></div>
          )}
        </main>
      </div>
    )
  }

  /* ---------- Cotización ---------- */
  if (vista.p === 'cot' && cot) {
    const val = porCotizacion(cot, ctx)
    const r = rentabilidad(cot, ctx)
    const ajustada = cot.aplicarUAFIR !== false && r.factorUAFIR > 1.001
    return (
      <div className="app">
        <Barra titulo={cot.nombre} migaja={cliente?.nombre}
          atras={() => setVista(
            vista.de === 'casa' && cot.casa ? { p: 'casa', cli: cot.cliente, casa: cot.casa, de: 'cliente' }
            : (vista.de === 'inicio' || vista.de === 'visita') ? { p: 'inicio' }
            : { p: 'cotizaciones' })}
          acciones={<>
            <button className={`ib sm ico ${interno ? 'pri' : ''}`} onClick={() => setInterno(v => !v)}
              title="Vista interna — márgenes y rentabilidad" aria-pressed={interno}
              aria-label={interno ? 'Ocultar la vista interna' : 'Mostrar la vista interna'}>
              <Icono d={<>
                <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
                <circle cx="12" cy="12" r="2.6" />
                {!interno && <path d="M4 20 20 4" />}
              </>} />
            </button>
            <button type="button" className="ib pri ico" aria-label="Nueva habitación"
              onClick={() => setCaptura({ tipo: 'habitacion' })}><IconoMas /></button>
          </>} />
        <main>
          {/* SIN texto pre-llenado (crítica 2026-08-20): «Recámara» de fábrica era un
              dato inventado a un toque de guardarse. La sugerencia va como placeholder,
              que no se puede guardar sin teclear. */}
          {captura?.tipo === 'habitacion' && (
            <Captura etiqueta="Nombre de la habitación" sugerencia="Sala, recámara, cocina…"
              onCancelar={() => setCaptura(null)}
              onConfirmar={async n => {
                setCaptura(null)
                const copia = structuredClone(cot)
                copia.habitaciones.push({ id: store.nuevoId(), nombre: n, items: [] })
                await guardarCot(copia)
              }} />
          )}
          <Comparativo cot={cot} val={val} ctx={ctx} />
          {/* La contabilidad que protege el margen vive DETRÁS de la vista interna, entera.
              El cliente a veces mira esta pantalla, y el UAFIR, el objetivo y el factor de
              ajuste son exactamente lo que nunca debe leer: Principio 3 de PRODUCT.md. */}
          {interno && (
            <div className="card" style={{ borderColor: 'var(--alto)' }}>
              <div className="sec" style={{ margin: '0 0 6px', color: 'var(--alto)' }}>
                Solo interno — rentabilidad
              </div>
              {ajustada && (
                <div className="nota acento" style={{ marginTop: 4 }}>
                  <b>Protección de rentabilidad activa.</b> A precio de lista esta cotización daba{' '}
                  {pct(r.uafirPct)} de UAFIR, bajo el {pct(r.objetivo)} objetivo. El precio se ajustó
                  ×{r.factorUAFIR.toFixed(3)} para alcanzarlo.
                </div>
              )}
              <div className="ln"><span>Precio de venta</span><b>{mx(val.total.total)}</b></div>
              <div className="ln">
                <span>Costo de operación <Sello>Sin calibrar</Sello></span>
                <b className="cifra-prov">{mx(r.costoOp)}</b>
              </div>
              <div className="ln"><span>Gastos de administración</span><b>{mx(r.gastosAdmin)}</b></div>
              <div className="ln"><span>Comisión de venta</span><b>{mx(r.comisionVenta)}</b></div>
              <div className="ln big" style={{ borderTop: '1px solid var(--linea)', marginTop: 6, paddingTop: 6 }}>
                <span style={{ color: 'var(--negro)', fontWeight: 500 }}>
                  UAFIR{ajustada ? ' a precio de lista' : ''}
                </span>
                <b>{mx(r.uafir)} · {pct(r.uafirPct)}</b>
              </div>
              <div className="prov" style={{ margin: '12px 0 0' }}>
                El gasto fijo diario ({mx(ctx.f.CostoDiaMetropolitano)} al día) y el factor de cada
                producto son <b>provisionales</b>: todavía no salen de la operación real. Mientras no
                se calibren, este costo —y con él el UAFIR y el ajuste automático de precio— puede
                estar corrido en cualquier dirección.
              </div>
            </div>
          )}
          <div className="sec">Habitaciones</div>
          {/* Leyenda adyacente de las siglas (crítica 2026-08-20): E1/E2/E3 no se
              explicaban en ningún lado de la pantalla que las usa. */}
          {(cot.habitaciones || []).some(h => (h.items || []).length) && (
            <div className="s" style={{ marginBottom: 14 }}>
              {[1, 2, 3].map(e => `${abrev(cot, e)} = ${rotulo(cot, e)}`).join(' · ')}
            </div>
          )}
          {(cot.habitaciones || []).length ? cot.habitaciones.map(h => {
            const items = h.items || []
            const tot = items.reduce((a, x) => a + subtotal(porProducto(x, ctx)), 0)
            const marcas = [1, 2, 3].filter(e => items.some(x => x['e' + e]))
            return (
              <div className="card" key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'flex', gap: 3, flex: 'none' }}>
                  {items.slice(0, 3).map((x, i) =>
                    <Lamina key={i} src={ctx.P.get(x.sku)?.img} clase="ph xs" />)}
                  {!items.length && <div className="ph xs" />}
                </div>
                <div className="grow tap" {...tapProps(() => setVista({ ...vista, p: 'hab', hab: h.id }))}>
                  <div className="t">{h.nombre}</div>
                  <div className="s">{plural(items.length, 'producto', 'productos')}</div>
                  <div style={{ display: 'flex', gap: 5, marginTop: 7, flexWrap: 'wrap' }}>
                    {marcas.map(e => (
                      <span className="bdg" key={e} style={{ background: `var(--b${e})` }}>{abrev(cot, e)}</span>
                    ))}
                  </div>
                </div>
                <div className="money">{mx(tot)}</div>
              </div>
            )
          }) : <div className="empty"><b>Sin habitaciones todavía.</b><p>El alcance se arma por habitación: sala, recámara, cocina. Toca el botón de arriba para agregar la primera.</p></div>}
          <div className="sec">Cotización</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {/* Las tres salidas del cierre de un rubro (Teoría §1 v2, punto 4): otro
                rubro en esta casa, presentar (Documentos), o terminar por hoy (volver). */}
            {/* La central del cotizador (F3): se entra al plano y desde ahí se captura todo. */}
            <button type="button" className="ib pri" style={{ gridColumn: '1 / -1' }}
              onClick={() => setVista({ ...vista, p: 'plano' })}>
              Plano de la casa — recorrer y capturar
            </button>
            <button type="button" className="ib" onClick={() => cot.casa && setVista({ p: 'casa', cli: cot.cliente, casa: cot.casa, de: 'cot', cot: cot.id, origen: vista.de })}>
              Datos de la casa
            </button>
            <button type="button" className="ib" onClick={() => setVista({ p: 'cliente', cli: cot.cliente, de: 'cot', casa: cot.casa, cot: cot.id, origen: vista.de })}>
              Datos del cliente
            </button>
            <button type="button" className="ib" style={{ gridColumn: '1 / -1' }}
              onClick={() => setVista({ ...vista, p: 'rubros' })}>
              Otro rubro en esta casa
            </button>
            {(cot.habitaciones || []).some(h => h.recinto) && (
              <button type="button" className="ib" style={{ gridColumn: '1 / -1' }} onClick={calcularRecorrido}>
                Recalcular las partidas del recorrido
              </button>
            )}
            <button type="button" className="ib" style={{ gridColumn: '1 / -1' }} onClick={terminarPorHoy}>
              Terminar por hoy
            </button>
            <button className="ib" onClick={() => setVista({ ...vista, p: 'ajustes' })}>Ajustes</button>
            <button className="ib" onClick={() => setVista({ ...vista, p: 'notas' })}>Notas</button>
            <button className="ib" onClick={() => setVista({ ...vista, p: 'firma' })}>
              {cot.firmaCliente ? 'Firma registrada' : 'Firmar'}
            </button>
            <button type="button" className="ib pri" style={{ gridColumn: '1 / -1' }}
              onClick={() => setVista({ ...vista, p: 'cierre' })}>Presentar al cliente</button>
            <button className="ib" onClick={() => setVista({ ...vista, p: 'docs' })}>Documentos</button>
            <button className="ib" onClick={() => setVista({ ...vista, p: 'bodega' })}>
              Bodega — salida y regreso
            </button>
            {piezasDeRamo(cot, 'taller').length > 0 && (
              <button className="ib" style={{ gridColumn: '1 / -1' }}
                onClick={() => setVista({ ...vista, p: 'encargo-taller' })}>
                Encargo al taller
              </button>
            )}
          </div>
        </main>
        {/* En modo opciones los importes NO se suman: se acota la opción mayor */}
        <Cuadro
          celdas={[
            { k: 'Cliente', v: cliente?.nombre || '—', chica: false },
            { k: 'Presentación', v: esOpciones(cot) ? 'Opciones' : 'Etapas' },
            { k: 'Días de obra', v: val.total.dias }
          ]}
          importe={esOpciones(cot) ? Math.max(val[1].total, val[2].total, val[3].total) : val.total.total}
          rotuloImporte={esOpciones(cot) ? 'Opción mayor' : 'Proyecto completo'} />
      </div>
    )
  }

  /* ---------- El cierre: el vendedor voltea el teléfono (E5) ---------- */
  if (vista.p === 'cierre' && cot) {
    return (
      <Cierre cot={cot} cliente={cliente} ctx={ctx} objetos={cot.objetos || {}}
        intervenciones={intervencionesCasa} protocoloRevisionId={PROTOCOLO_REVISION.id}
        Barra={Barra}
        atras={() => setVista({ ...vista, p: 'cot' })}
        onRecomendar={e => guardarCot({ ...cot, recomendada: e })}
        onFirmar={() => setVista({ ...vista, p: 'firma' })}
        onDocs={() => setVista({ ...vista, p: 'docs' })}
        onAprobadoPorMensaje={() => guardarCot({ ...cot, aprobacion: { via: 'mensaje', en: new Date().toISOString(), por: usuario()?.email || 'local' } })} />
    )
  }

  /* ---------- Bitácora de la casa: intervenciones y garantías ---------- */
  if (vista.p === 'bitacora' && casa) {
    return (
      <VistaBitacora casa={casa} cliente={cliente}
        cotizaciones={cotizaciones.filter(x => x.casa === casa.id)}
        protocolos={protocolos} herramientas={herramientas}
        atras={() => setVista(vista.de === 'inicio'
          ? { p: 'inicio' }
          : { p: 'casa', cli: vista.cli, casa: casa.id })} />
    )
  }

  /* ---------- Bodega: salida y regreso de la orden ---------- */
  if (vista.p === 'bodega' && cot) {
    return (
      <Bodega cot={cot} ctx={ctx} herramientas={herramientas}
        atras={() => setVista(vista.de === 'inicio'
          ? { p: 'inicio' }
          : { ...vista, p: 'cot' })} />
    )
  }

  /* ---------- Encargo al taller: la mesa de trabajo con el aliado ---------- */
  /* Se llega por dos puertas —la cotización o el módulo Encargos— y el regreso
     respeta por cuál se entró. */
  if (vista.p === 'encargo-taller' && cot) {
    return (
      <VistaEncargoTaller cot={cot} resolver={s => ctx.P.get(s)}
        atras={() => setVista(vista.de === 'encargos'
          ? { p: 'encargos' }
          : { ...vista, p: 'cot' })} />
    )
  }

  /* ---------- Ajustes de la cotización ---------- */
  if (vista.p === 'ajustes' && cot) {
    const campo = (llave, etiqueta, tipo = 'number') => (
      <label className="f"><span>{etiqueta}</span>
        <input type={tipo} defaultValue={cot[llave] ?? 0}
          onBlur={e => guardarCot({ ...cot, [llave]: parseFloat(e.target.value) || 0 })} /></label>
    )
    return (
      <div className="app">
        <Barra titulo="Ajustes" migaja={cot.nombre} atras={() => setVista({ ...vista, p: 'cot' })} />
        <main>
          <div className="card">
            <label className="f"><span>Cómo se presentan los tres bloques</span>
              <select value={cot.modo || 'etapas'}
                onChange={e => guardarCot({ ...cot, modo: e.target.value })}>
                <option value="etapas">Etapas — el cliente compra las tres en secuencia, el total suma</option>
                <option value="opciones">Opciones — el cliente elige una, los importes no se suman</option>
              </select>
            </label>
            <div className="s" style={{ marginTop: -4, marginBottom: 12, lineHeight: 1.45 }}>
              {esOpciones(cot)
                ? 'Un producto marcado en varias opciones va incluido en todas ellas. La protección de rentabilidad evalúa cada opción por separado y se rige por la más floja, para que ninguna se venda bajo el objetivo.'
                : 'Un producto marcado en varias etapas se ejecuta en cada una de ellas y suma en cada una. La rentabilidad se mide sobre el proyecto completo.'}
            </div>
            {[1, 2, 3].map(e => (
              <label className="f" key={e}>
                <span>Nombre del bloque {e}</span>
                <input defaultValue={cot.etiquetas?.[e] || ''} placeholder={rotulo(cot, e)}
                  onBlur={ev => guardarCot({
                    ...cot, etiquetas: { ...(cot.etiquetas || {}), [e]: ev.target.value }
                  })} />
              </label>
            ))}
          </div>
          <div className="card">
            <label className="f"><span>Canal de venta</span>
              <select value={cot.equipo} onChange={e => {
                const eq = equipos.find(x => x.id === e.target.value)
                guardarCot({ ...cot, equipo: eq.id, distancia: eq.distancia, minTraslado: eq.tiempo })
              }}>
                {equipos.map(e => (
                  <option key={e.id} value={e.id}>
                    {e.nombre} — {e.proporcionDeComision}%{e.distribuidor ? ' (distribuidor)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <div className="two">
              {campo('distancia', 'Distancia (km)')}
              {campo('minTraslado', 'Traslado (min)')}
            </div>
            {campo('peaje', 'Casetas y hospedaje por etapa (MXN)')}
          </div>
          <div className="sec">Rentabilidad</div>
          <div className="card">
            {campo('uafir', 'UAFIR objetivo (%)')}
            {campo('sobrePrecio', 'Sobreprecio manual (%)')}
            <label className="f" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
              <input type="checkbox" checked={cot.aplicarUAFIR !== false}
                style={{ width: 20, height: 20 }}
                onChange={e => guardarCot({ ...cot, aplicarUAFIR: e.target.checked })} />
              <span style={{ margin: 0 }}>Aplicar protección de rentabilidad</span>
            </label>
            <div className="s" style={{ marginTop: 10 }}>
              Si se desactiva, el precio se queda en lista aunque el UAFIR quede por debajo
              del objetivo. El sobreprecio se aplica siempre, encima del factor.
            </div>
          </div>
        </main>
      </div>
    )
  }

  /* ---------- Notas ---------- */
  if (vista.p === 'notas' && cot) return (
    <div className="app">
      <Barra titulo="Notas" migaja={cot.nombre} atras={() => setVista({ ...vista, p: 'cot' })} />
      <main>
        <div className="card">
          <label className="f" style={{ margin: 0 }}>
            <span>Notas especiales — salen impresas en la propuesta y en la orden de servicio</span>
            <textarea defaultValue={cot.notasCot || ''} style={{ minHeight: 150 }}
              onBlur={e => guardarCot({ ...cot, notasCot: e.target.value })} />
          </label>
        </div>
      </main>
    </div>
  )

  /* ---------- Firma ---------- */
  if (vista.p === 'firma' && cot) return (
    <div className="app">
      <Barra titulo="Firma" migaja={cot.nombre} atras={() => setVista({ ...vista, p: 'cot' })} />
      <main>
        <Firma firmaPrevia={cot.firmaCliente} nombreSugerido={cot.firmante || cliente?.nombre}
          onGuardar={async (png, quien) => {
            await guardarCot({ ...cot, firmaCliente: png, firmante: quien, fechaFirma: hoy() })
            setVista({ ...vista, p: 'cot' })
          }} />
      </main>
    </div>
  )

  /* ---------- Documentos ---------- */
  if (vista.p === 'docs' && cot) {
    const val = porCotizacion(cot, ctx)
    return (
      <div className="app">
        <Barra titulo="Documentos" migaja={cot.nombre} atras={() => setVista({ ...vista, p: 'cot' })} />
        <main>
          <Documentos cot={cot} cliente={cliente} ctx={ctx} equipos={equipos} herramientas={herramientas}
            credenciales={credencialesCasa}
            comparativo={<Comparativo cot={cot} val={val} ctx={ctx} />} />
        </main>
      </div>
    )
  }

  /* ---------- Habitación ---------- */
  if (vista.p === 'hab' && hab) {
    const items = hab.items || []
    const total = items.reduce((a, x) => a + subtotal(porProducto(x, ctx)), 0)
    const marcarTodo = async e => {
      const encendido = items.every(x => x['e' + e])
      await mutarHab(h => h.items.forEach(x => { x['e' + e] = !encendido }))
    }
    const abrirCatalogo = () => {
      setVista({ ...vista, p: 'add' }); setCarrito({}); setCategoria(null); setPestana(0); setBusqueda('')
    }
    return (
      <div className="app">
        <Barra titulo={hab.nombre} migaja={cot.nombre}
          atras={() => setVista({ ...vista, p: vista.de === 'plano' ? 'plano' : 'cot', hab: null })}
          acciones={<Sync s={sync} />} />
        <main>
          {/* EL CONTENIDO DE LA HABITACIÓN VA PRIMERO — P0 de la crítica del
              2026-08-20: los renglones propios quedaban en y=1,969, enterrados
              bajo seis tarjetas promocionales de levantamiento. La tarea manda
              (Regla 2 de la teoría); los caminos para AGREGAR van después del
              contenido, y la acción principal vive en la barra inferior, en la
              zona del pulgar. */}
          {/* LA PALETA (E2): si esta habitación es un recinto del plano, lo primero es
              lo que se marca de pie — lo que hay, lo que se propone, los hallazgos
              con foto, la nota dictada. Cada toque escribe un objeto de la
              cotización por id, de inmediato. Las partidas (abajo) las produce la
              calculadora en E4; hoy se siguen agregando del catálogo. */}
          {hab.recinto && (
            <PaletaElectrica recinto={hab.recinto} nombre={hab.nombre} cotId={cot.id}
              objetos={cot.objetos || {}}
              onObjeto={(id, valor) => valor === null
                ? store.borrarCampo('cotizaciones', cot.id, `objetos.${id}`)
                : store.guardarParcial('cotizaciones', cot.id, { objetos: { [id]: valor } })}
              fotos={cot.fotosRecinto?.[hab.recinto] || []}
              onFotos={lista => store.guardarParcial('cotizaciones', cot.id, { fotosRecinto: { [hab.recinto]: lista } })}
              nota={cot.notasRecinto?.[hab.recinto] || ''}
              onNota={texto => store.guardarParcial('cotizaciones', cot.id, { notasRecinto: { [hab.recinto]: texto } })} />
          )}
          {hab.recinto && (
            <>
              <div className="sec">Partidas de {hab.nombre}</div>
              {/* De lo propuesto a las partidas, con el cable medido desde el tablero.
                  Se puede recalcular cuantas veces haga falta: solo toca lo que nació
                  aquí; lo agregado a mano del catálogo se queda. */}
              <button type="button" className={`ib w ${items.some(x => x.origen === 'paleta') ? '' : 'pri'}`}
                style={{ marginBottom: 'var(--e5)' }} onClick={() => calcularRecinto(hab.recinto)}
                disabled={!Object.values(cot.objetos || {}).some(o => o.recinto === hab.recinto && o.estado === 'propuesto' && o.sku)}>
                {items.some(x => x.origen === 'paleta') ? 'Recalcular las partidas de este recinto' : 'Calcular las partidas de este recinto'}
              </button>
            </>
          )}
          <div className="nota">
            {esOpciones(cot)
              ? 'Son alternativas: el cliente elige una. Un producto marcado en varias va incluido en todas ellas.'
              : 'Las etapas son acumulativas: la 1 es la propuesta económica, 1+2 la intermedia y las tres el proyecto completo.'}
          </div>
          {items.length > 0 && (
            <div className="card" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div className="s grow">Marcar toda la habitación en</div>
              {[1, 2, 3].map(e => (
                <button type="button" className="ib sm" key={e} onClick={() => marcarTodo(e)}>{rotuloCorto(cot, e)}</button>
              ))}
            </div>
          )}
          {items.length ? items.map((it, i) => {
            const b = ctx.P.get(it.sku) || { nombre: it.sku }
            const v = porProducto(it, ctx)
            return (
              <div className="card" key={i}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 11 }}>
                  <Lamina src={b.img} />
                  <div className="grow">
                    <div className="t">{b.nombre}</div>
                    <div className="s">
                      {TODAS_CATEGORIAS.find(c => c.id === b.cat)?.nombre || 'Concepto especial'}
                      {b.porDefinir ? '' : ` · ${v.minutos} min de instalación`}
                      {it.origen === 'paleta' && ' · del recorrido'}
                    </div>
                  </div>
                  <button className="ib sm dz" aria-label={`Quitar ${b.nombre}`}
                    onClick={() => mutarHab(h => h.items.splice(i, 1))}>×</button>
                </div>

                {/* LO QUE TRAE UNA PARTIDA DE LEVANTAMIENTO.
                    Estos tres campos ya viajaban dentro del renglón desde que se conectó
                    el levantamiento a la cotización, y NO se mostraban en ningún lado: el
                    dato llegaba y era invisible. Sin esto, una partida de 83 mil pesos se
                    ve idéntica a cualquier otra y nadie recuerda que su mano de obra salió
                    de distancias que nunca se midieron. */}
                {b.resumen && (
                  <div className="s" style={{ marginBottom: 10 }}>
                    {b.resumen.join(' · ')}
                  </div>
                )}
                {b.supuestos?.length > 0 && (
                  <div className="warn leible" style={{ marginBottom: 10 }}>
                    <b>Ojo con lo que este número supone:</b>
                    <ul style={{ margin: '6px 0 0', paddingLeft: '1.1rem' }}>
                      {b.supuestos.map((s, n) => <li key={n}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {b.pendientes?.length > 0 && (
                  <div className="warn leible" style={{ marginBottom: 10 }}>
                    <b>Sin cobrar por falta de catálogo:</b>{' '}
                    {b.pendientes.map(p => `${p.concepto} (${p.cantidad} ${p.unidad})`).join(', ')}.
                    Se dan de alta en Configuración → Insumos y se vuelve a levantar.
                  </div>
                )}

                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 11 }}>
                  <div className="qty">
                    <button aria-label="Quitar uno"
                      onClick={() => mutarHab(h => { h.items[i].q = Math.max(1, h.items[i].q - 1) })}>−</button>
                    <span>{it.q}</span>
                    <button aria-label="Agregar uno"
                      onClick={() => mutarHab(h => { h.items[i].q += 1 })}>+</button>
                  </div>
                  <div className="grow" />
                  {b.porDefinir ? <Sello>Por definir · vía de proyecto</Sello>
                                : <div className="money">{mx(subtotal(v))}</div>}
                </div>
                {/* Fotos del levantamiento, pegadas al renglón que las explica */}
                {/* Quitar una foto es en dos toques dentro de la lámina, no con el
                    diálogo del sistema: el primero la marca, el segundo la anula. */}
                <div className="fotos">
                  {(it.fotos || []).map((f, j) => {
                    const clave = `${i}-${j}`
                    const dudosa = fotoDudosa === clave
                    return (
                      <span className={`foto${dudosa ? ' dudosa' : ''}`} key={j}>
                        <img src={f} alt={`Foto ${j + 1} de ${b.nombre}`}
                          onClick={() => setFotoDudosa(dudosa ? null : clave)} />
                        {dudosa && (
                          <button className="anular" onClick={async () => {
                            setFotoDudosa(null)
                            await mutarHab(h => h.items[i].fotos.splice(j, 1))
                          }}>Anular</button>
                        )}
                      </span>
                    )
                  })}
                  <label className="add">+
                    <input type="file" accept="image/*" capture="environment" hidden
                      onChange={async e => {
                        const archivo = e.target.files?.[0]; if (!archivo) return
                        const chica = await store.comprimirImagen(archivo)
                        await mutarHab(h => {
                          h.items[i].fotos = h.items[i].fotos || []
                          h.items[i].fotos.push(chica)
                        })
                        e.target.value = ''
                      }} />
                  </label>
                </div>
                <div className="etapas">
                  {[1, 2, 3].map(e => (
                    <button key={e} className={`et ${it['e' + e] ? 'on' + e : ''}`}
                      onClick={() => mutarHab(h => { h.items[i]['e' + e] = !h.items[i]['e' + e] })}>
                      {rotuloCorto(cot, e)}
                    </button>
                  ))}
                </div>
              </div>
            )
          }) : (
            <div className="empty">
              <b>Esta habitación está vacía.</b>
              <p>Toca «Agregar productos» abajo para abrir el catálogo, o corre un
                 levantamiento guiado: mide la habitación paso a paso y agrega la
                 partida completa, con material y mano de obra.</p>
            </div>
          )}

          {/* Los levantamientos guiados, DENTRO de la habitación: abiertos desde aquí
              sí saben a qué cotización guardar. Van DESPUÉS del contenido y como fila
              compacta — producen una partida a la medida, pero no pueden enterrar lo
              que la habitación ya tiene. */}
          <div className="sec">Levantamiento guiado</div>
          <div className="nota">
            Te lleva paso por paso, te dice qué medir y con qué, y al final agrega aquí
            la partida completa: material, mano de obra y el porqué de cada número.
          </div>
          <div className="fila-pills">
            {LEVANTAMIENTOS.map(l => (
              <button type="button" className="ib" key={l.p}
                onClick={() => setVista({ ...vista, p: l.p })}>{l.n}</button>
            ))}
          </div>
        </main>
        {/* La barra de trabajo carga la ACCIÓN principal de esta pantalla —abrir el
            catálogo— junto al importe vivo: es lo que el pulgar alcanza sin estirarse. */}
        <div className="fab"><div className="in">
          <div className="cuadro">
            <div className="celda oculta-chica">
              <span className="k">Renglones</span>
              <span className="v">{items.length}</span>
            </div>
            <div className="celda importe">
              <span className="k">Esta habitación</span>
              <Precio valor={total} chico />
            </div>
          </div>
          <button type="button" onClick={abrirCatalogo}>Agregar productos</button>
        </div></div>
      </div>
    )
  }

  /* ---------- Agregar productos ---------- */
  if (vista.p === 'add' && hab) {
    const enCarrito = Object.values(carrito).reduce((a, b) => a + b, 0)
    const q = busqueda.trim().toLowerCase()
    const buscando = q.length >= 2
    // El catálogo completo se navega, incluidas las piezas del taller: la fase es
    // información, no candado. Lo que sí distingue el sistema es cuáles tienen precio.
    const lista = buscando
      ? TODOS_PRODUCTOS.filter(p => p.nombre.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q))
      : categoria ? TODOS_PRODUCTOS.filter(p => p.cat === categoria) : []

    const confirmar = async () => {
      await mutarHab(h => {
        for (const [sku, cantidad] of Object.entries(carrito))
          if (cantidad > 0) h.items.push({ sku, q: cantidad, e1: true, e2: false, e3: false, fotos: [] })
      })
      setCarrito({}); setVista({ ...vista, p: 'hab' })
    }

    return (
      <div className="app">
        <Barra titulo={buscando ? 'Búsqueda' : categoria ? TODAS_CATEGORIAS.find(c => c.id === categoria).nombre : 'Catálogo'}
          migaja={hab.nombre}
          atras={() => {
            if (buscando) return setBusqueda('')
            if (categoria) return setCategoria(null)
            setVista({ ...vista, p: 'hab' })
          }} />
        <main>
          <div className="tabs">
            {['Categorías', 'Paquetes', 'Especial'].map((n, i) => (
              <button key={i} className={`tb ${pestana === i ? 'on' : ''}`}
                onClick={() => { setPestana(i); setCategoria(null); setBusqueda('') }}>{n}</button>
            ))}
          </div>

          {pestana === 0 && (
            <>
              {/* Búsqueda global: encontrar sin navegar categorías */}
              <input placeholder="Buscar producto en todo el catálogo" value={busqueda}
                onChange={e => setBusqueda(e.target.value)} style={{ marginBottom: 12 }} />
              {!buscando && !categoria && (() => {
                const tarjeta = c => {
                  const n = TODOS_PRODUCTOS.filter(p => p.cat === c.id).length
                  const sel = Object.entries(carrito)
                    .filter(([sku, v]) => v > 0 && ctx.P.get(sku)?.cat === c.id)
                    .reduce((a, [, v]) => a + v, 0)
                  // La fotografía de marca manda donde ilustra de verdad esa categoría.
                  // Donde no la hay va el gráfico autorado de `graficos.js` —un recorte
                  // del material a escala—, nunca una placa vacía con el nombre: ese
                  // hueco beige era lo que hacía ver el catálogo como software sin
                  // terminar. Ninguna categoría queda sin imagen y ninguna la repite.
                  const imagen = fotoDe(c.id) || c.img
                  return (
                    <button type="button" className={`gc ${sel ? 'sel' : ''}`} key={c.id}
                      onClick={() => setCategoria(c.id)}
                      aria-label={`${c.nombre} — ${plural(n, 'producto', 'productos')}`}>
                      {sel > 0 && <div className="cnt">{sel}</div>}
                      <img className="ph" alt="" src={imagen} loading="lazy" />
                      <div className="b">
                        <div className="n">{c.nombre}</div>
                        <div className="s">{plural(n, 'producto', 'productos')}</div>
                      </div>
                    </button>
                  )
                }
                return (
                  <>
                    <div className="grid sangre">{CATEGORIAS.map(tarjeta)}</div>
                    <div className="sec">Fabricación del taller</div>
                    <div className="warn leible">
                      Las {PRODUCTOS_TALLER.length} piezas del taller se cotizan por la{' '}
                      <b>vía de proyecto</b>: se levanta la medida y se firma anticipo de diseño.
                      Todavía no tienen precio ni tiempos capturados, así que se pueden agregar a
                      la habitación pero <b>no suman importe</b> hasta que el taller los defina.
                    </div>
                    <div className="grid sangre">{CATEGORIAS_TALLER.map(tarjeta)}</div>
                  </>
                )
              })()}
              {/* Los productos van en mosaico, igual que las categorías: la imagen manda
                  y el nombre va encima del material. Antes era un renglón con miniatura
                  de 76 px, importe a la derecha e hilo gris debajo — que es la forma de
                  un estado de cuenta, no la de un catálogo que se enseña a un cliente.
                  Los controles de cantidad NO se esconden tras un toque en el mosaico:
                  un toque equivocado aquí cuesta dinero, así que suman y restan explícito. */}
              {(buscando || categoria) && (lista.length ? (
                <div className="grid productos sangre">
                  {lista.map(p => {
                    const v = porProducto({ sku: p.sku, q: 1 }, ctx)
                    const q = carrito[p.sku] || 0
                    return (
                      <div className={`gc gp ${q ? 'sel' : ''}`} key={p.sku}>
                        {q > 0 && <div className="cnt">{q}</div>}
                        <img className="ph" alt="" src={p.img} loading="lazy" />
                        <div className="b">
                          <div className="n">{p.nombre}</div>
                          <div className="s">
                            {p.porDefinir
                              ? <>Por {p.unidad === 'm2' ? 'm²' : p.unidad === 'ml' ? 'metro lineal' : 'pieza'} <Sello />
                                  {/* `agregada` marca las piezas que Claude propuso y el taller
                                      todavía no confirmó que fabrique. Se dice, no se esconde. */}
                                  {p.agregada && <Sello>Propuesta, sin confirmar con el taller</Sello>}</>
                              : <>Se instala en {p.tIns + p.tCfg} min · {mx(subtotal(v))} c/u</>}
                          </div>
                          <div className="qty sobre">
                            <button aria-label={`Quitar uno de ${p.nombre}`}
                              onClick={() => setCarrito(c => ({ ...c, [p.sku]: Math.max(0, (c[p.sku] || 0) - 1) }))}>−</button>
                            <span>{q}</span>
                            <button aria-label={`Agregar uno de ${p.nombre}`}
                              onClick={() => setCarrito(c => ({ ...c, [p.sku]: (c[p.sku] || 0) + 1 }))}>+</button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : <div className="empty"><b>Ningún producto coincide con «{busqueda}».</b><p>Se busca en todo el catálogo, incluidas las piezas del taller.</p></div>)}
            </>
          )}

          {pestana === 1 && (
            <div className="grid">
              {PAQUETES.map(p => (
                <div className="gc" key={p.id} {...tapProps(async () => {
                  await mutarHab(h => p.items.forEach(i =>
                    h.items.push({ sku: i.sku, q: i.q, e1: true, e2: false, e3: false, fotos: [] })))
                  setVista({ ...vista, p: 'hab' })
                })}>
                  <Lamina src={ctx.P.get(p.items[0].sku)?.img} clase="ph lg" />
                  <div className="b"><div className="n">{p.nombre}</div><div className="s">{p.det}</div></div>
                </div>
              ))}
            </div>
          )}

          {/* El especial viaja DENTRO del renglón, en `def`: así vive solo en esta
              cotización —lo que promete la pantalla— y sobrevive a recargar la página. */}
          {pestana === 2 && <Especial onAgregar={async esp => {
            await mutarHab(h => h.items.push({
              sku: esp.sku, q: 1, e1: true, e2: false, e3: false, fotos: [], def: esp
            }))
            setVista({ ...vista, p: 'hab' })
          }} />}
        </main>

        {enCarrito > 0 && (
          <div className="fab"><div className="in">
            <button className="gh" onClick={() => setCarrito({})} aria-label="Vaciar">×</button>
            <button onClick={confirmar}>
              Agregar {enCarrito} producto{enCarrito > 1 ? 's' : ''} a {hab.nombre}
            </button>
          </div></div>
        )}
      </div>
    )
  }

  /* Una cotización recién creada tarda un instante en llegar por la suscripción:
     mientras, no se enseña la pantalla de «te perdiste», se espera. */
  if (vista.cot && !cot) return <div className="app"><div className="empty">Abriendo la cotización…</div></div>
  return <div className="app"><div className="empty">
    <button className="ib" onClick={() => setVista({ p: 'inicio' })}>Volver al inicio</button>
  </div></div>
}

function Especial({ onAgregar }) {
  const [nombre, setNombre] = useState('')
  const [precio, setPrecio] = useState(0)
  const [minutos, setMinutos] = useState(60)
  return (
    <div className="card">
      <label className="f"><span>Nombre del concepto</span>
        <input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Trabajo especial" /></label>
      <div className="two">
        <label className="f"><span>Precio de venta</span>
          <input type="number" value={precio} onChange={e => setPrecio(+e.target.value)} /></label>
        <label className="f"><span>Minutos</span>
          <input type="number" value={minutos} onChange={e => setMinutos(+e.target.value)} /></label>
      </div>
      <button className="ib pri w" disabled={!nombre.trim()} onClick={() => onAgregar({
        sku: 'ESP-' + store.nuevoId(), nombre: nombre.trim(), cat: 'dia',
        tIns: minutos, tCfg: 0, precio, factor: 0.1, comp: [],
        img: CATEGORIAS.find(c => c.id === 'dia').img, especial: true
      })}>Agregar</button>
      <div className="s" style={{ marginTop: 10 }}>
        Los especiales no entran al catálogo permanente: viven en esta cotización.
      </div>
    </div>
  )
}
